import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './api/axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Header from './Header';
import { useAuth } from './AuthContext';
import { useUploadProgress } from './UploadProgressContext';
import { getSubFileOptions, reconcileFileSubtypes } from './interfaceFiles';
import { FaFolderOpen, FaFileCsv, FaFilePdf, FaUpload, FaTimes, FaSpinner, FaCheckCircle, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';

// --- UPDATED: Comprehensive Cross-Validation Rules List ---
const ALL_CROSS_RULES = [
    // Base Master Data
    { id: 'pl_prod', dependent: 'product_locations', reference: 'products', label: 'Product Locations vs Products' },
    { id: 'pl_sup', dependent: 'product_locations', reference: 'suppliers', label: 'Product Locations vs Suppliers' },
    { id: 'pl_loc', dependent: 'product_locations', reference: 'locations', label: 'Product Locations vs Locations' },
    { id: 'pg_prod', dependent: 'products', reference: 'product_groups', label: 'Products vs Product Groups' },
    { id: 'sup_loc', dependent: 'suppliers', reference: 'locations', label: 'Suppliers vs Locations' },
    { id: 'bom_prod', dependent: 'bill_of_materials', reference: 'products', label: 'Bill of Materials vs Products' },
    { id: 'bom_pl', dependent: 'bill_of_materials', reference: 'product_locations', label: 'Bill of Materials vs Product Locations' },

    // Campaigns
    { id: 'plc_camp', dependent: 'product_location_campaigns', reference: 'campaigns', label: 'Product Location Campaigns vs Campaigns' },

    // Composite Checks (Product + Location vs Product Locations)
    { id: 'plc_pl', dependent: 'product_location_campaigns', reference: 'product_locations', label: 'Product Location Campaigns vs Product Locations' },
    { id: 'plpc_pl', dependent: 'product_location_price_calendars', reference: 'product_locations', label: 'Product Loc Price Calendars vs Product Locations' },
    { id: 'plbs_pl', dependent: 'product_location_batch_sizes', reference: 'product_locations', label: 'Product Loc Batch Sizes vs Product Locations' },
    { id: 'plbs_sup', dependent: 'product_location_batch_sizes', reference: 'suppliers', label: 'Product Loc Batch Sizes vs Suppliers' },
    { id: 'plbs_pbs', dependent: 'product_location_batch_sizes', reference: 'product_batch_sizes', label: 'Product Loc Batch Sizes vs Product Batch Sizes' },
    { id: 'pls_pl', dependent: 'product_location_suppliers', reference: 'product_locations', label: 'Product Loc Suppliers vs Product Locations' },
    { id: 'plsra_pl', dependent: 'product_location_supplier_replenishment_agreements', reference: 'product_locations', label: 'Product Loc Supplier Repl. Agreements vs Product Locations' },
    { id: 'plcg_pl', dependent: 'product_location_coordination_groups', reference: 'product_locations', label: 'Product Loc Coordination Groups vs Product Locations' },
    { id: 'pro_as_pl', dependent: 'promotion_assortments', reference: 'product_locations', label: 'Promotion Assortments vs Product Locations' },
    { id: 'pec_pl', dependent: 'product_end_customers', reference: 'product_locations', label: 'Product End Customers vs Product Locations' },

    // Transactions vs Product Locations
    { id: 'bal_pl', dependent: 'balances', reference: 'product_locations', label: 'Balances vs Product Locations' },
    { id: 'sales_pl', dependent: 'sales', reference: 'product_locations', label: 'Sales vs Product Locations' },
    { id: 'del_pl', dependent: 'deliveries', reference: 'product_locations', label: 'Deliveries vs Product Locations' },
    { id: 'spoil_pl', dependent: 'spoilages', reference: 'product_locations', label: 'Spoilages vs Product Locations' },
    { id: 'trans_pl', dependent: 'transfers', reference: 'product_locations', label: 'Transfers vs Product Locations' },
    { id: 'inv_pl', dependent: 'inventory_checks', reference: 'product_locations', label: 'Inventory Checks vs Product Locations' },
    { id: 'adj_pl', dependent: 'adjustments', reference: 'product_locations', label: 'Adjustments vs Product Locations' },
    { id: 'lost_pl', dependent: 'lost_sales', reference: 'product_locations', label: 'Lost Sales vs Product Locations' },
    { id: 'end_cust_pl', dependent: 'end_customer_transactions', reference: 'product_locations', label: 'End Customer Txns vs Product Locations' },
    { id: 'bb_pl', dependent: 'batch_balances', reference: 'product_locations', label: 'Batch Balances vs Product Locations' },
    { id: 'opo_pl', dependent: 'open_purchase_orders', reference: 'product_locations', label: 'Open Purchase Orders vs Product Locations' },
    { id: 'oso_pl', dependent: 'open_sales_orders', reference: 'product_locations', label: 'Open Sales Orders vs Product Locations' },
    { id: 'dc_tx_pl', dependent: 'dc_transactions', reference: 'product_locations', label: 'DC Transactions vs Product Locations' },
    { id: 'ord_pl', dependent: 'orders', reference: 'product_locations', label: 'Orders vs Product Locations' },

    // Footfalls vs Locations
    { id: 'foot_loc', dependent: 'footfalls', reference: 'locations', label: 'Footfalls vs Locations' }
];

// --- CHUNKED UPLOAD HELPERS ---
const CHUNK_SIZE = 50 * 1024 * 1024; // 50 MB — must match backend CHUNK_SIZE
const CONCURRENT_CHUNKS = 3;

// Sends each file to /api/upload-chunk/ in CHUNK_SIZE pieces, a few at a time, and returns
// { file, upload_id, total_chunks } per file, in the same order. Every request stays small,
// so none outlasts Railway's 5-minute upload window however big the file is.
// onProgress gets the fraction of chunks sent so far (0 to 1).
const sendFilesInChunks = async (files, signal, onProgress) => {
    let totalChunks = 0;
    files.forEach(file => { totalChunks += Math.ceil(file.size / CHUNK_SIZE) || 1; });
    let chunksUploaded = 0;
    const sent = [];

    for (const file of files) {
        const uploadId = crypto.randomUUID();
        const totalFileChunks = Math.ceil(file.size / CHUNK_SIZE) || 1;

        for (let i = 0; i < totalFileChunks; i += CONCURRENT_CHUNKS) {
            const batch = [];
            for (let j = i; j < Math.min(i + CONCURRENT_CHUNKS, totalFileChunks); j++) {
                const start = j * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunkForm = new FormData();
                chunkForm.append('upload_id', uploadId);
                chunkForm.append('chunk_index', j);
                chunkForm.append('filename', file.name);
                chunkForm.append('chunk', file.slice(start, end), file.name);
                batch.push(axiosInstance.post('/api/upload-chunk/', chunkForm, { signal }));
            }
            await Promise.all(batch);
            chunksUploaded += batch.length;
            onProgress(chunksUploaded / totalChunks);
        }

        sent.push({ file, upload_id: uploadId, total_chunks: totalFileChunks });
    }
    return sent;
};

const isCancelledRequest = (error) =>
    error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError';

// Message for a request that failed before the job started. No response at all means the
// connection itself dropped, so there's no server `detail` to show.
const describeStartFailure = (error, fallback) => {
    if (!error.response) return 'Lost connection to the server while sending files. Please check your connection and try again.';
    const detail = error.response.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
};

// --- AUDIO HELPER: Synthesize Notification Sounds ---
const playNotificationSound = (type = 'success') => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;

        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc.connect(gainNode);
        gainNode.connect(ctx.destination);

        if (type === 'success' || type === 'warning') { // Include warning in pleasant sounds
            osc.type = 'sine';
            osc.frequency.setValueAtTime(523.25, ctx.currentTime);
            osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1);
            gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.5);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        } else {
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(150, ctx.currentTime);
            gainNode.gain.setValueAtTime(0.05, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.5);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        }
    } catch (e) {
        console.error("Audio playback failed", e);
    }
};
// ----------------------------------------------------

// --- DYNAMIC Helper: Error Grouping Logic ---
const groupErrorItems = (rawItems, maxRowsPerPattern, maxPatterns) => {
    const grouped = {};
    const others = [];
    let backendMessage = null;
    let omittedRawCount = 0;

    rawItems.forEach(item => {
        if (item.includes("more errors omitted") || item.includes("more errors of this type omitted") || item.includes("more raw records omitted")) {
            backendMessage = item;
            const match = item.match(/(\d+)/);
            if (match) omittedRawCount = parseInt(match[1], 10);
            return;
        }

        const match = item.match(/^(?:Row|Line)\s+(\d+)(?:[:\s,]+)(.*)$/i);
        if (match) {
            const rowNum = parseInt(match[1], 10);
            const errorDesc = match[2].trim();

            if (!grouped[errorDesc]) grouped[errorDesc] = [];
            grouped[errorDesc].push(rowNum);
        } else {
            others.push(item);
        }
    });

    const formattedItems = [];
    let patternCount = others.length + Object.keys(grouped).length;
    let displayedCount = 0;

    // Process non-row errors first
    for (const item of others) {
        if (displayedCount < maxPatterns) {
            formattedItems.push(item);
            displayedCount++;
        }
    }

    // Process grouped row errors
    for (const [desc, rows] of Object.entries(grouped)) {
        if (displayedCount >= maxPatterns) break; // Strict Pattern Cutoff!

        rows.sort((a, b) => a - b);

        let displayedRows = rows;
        let remaining = 0;

        if (maxRowsPerPattern && maxRowsPerPattern !== Infinity && rows.length > maxRowsPerPattern) {
            displayedRows = rows.slice(0, maxRowsPerPattern);
            remaining = rows.length - maxRowsPerPattern;
        }

        const displayed = displayedRows.join(', ');
        const rowStr = remaining > 0
            ? `Rows ${displayed} and ${remaining.toLocaleString()} more rows`
            : `Rows ${displayed}`;

        if (rows.length === 1 && remaining === 0) {
            if (desc.includes("is an exact duplicate")) {
                formattedItems.push(`Line ${rows[0]} ${desc}`);
            } else {
                let cleanDesc = desc.replace(/^:\s*/, '');
                formattedItems.push(`Row ${rows[0]}: ${cleanDesc}`);
            }
        } else {
            if (desc.includes("is an exact duplicate")) {
                let pluralDesc = desc.replace("is an exact duplicate", "are exact duplicates");
                formattedItems.push(`${rowStr} ${pluralDesc}`);
            } else {
                let cleanDesc = desc.replace(/^:\s*/, '');
                formattedItems.push(`${rowStr}: ${cleanDesc}`);
            }
        }
        displayedCount++;
    }

    return {
        formattedItems,
        patternCount,
        hasBackendTruncation: !!backendMessage,
        omittedRawCount,
        omittedPatternCount: patternCount - displayedCount // Track exactly how many UI patterns we hid
    };
};

// --- Custom flat dropdown (cross-file rule selector) ---
const FlatDropdown = ({ value, options, onChange, disabled, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const selectedLabel = value ? options.find(o => o.value === value)?.label : null;

    return (
        <div className="custom-dropdown" ref={ref}>
            <button
                type="button"
                className={`custom-dropdown-trigger${isOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(p => !p)}
                disabled={disabled}
            >
                <span className={selectedLabel ? '' : 'custom-dropdown-placeholder'}>{selectedLabel || placeholder}</span>
                <span className="custom-dropdown-chevron">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
                <div className="custom-dropdown-list">
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            className={`custom-dropdown-option${value === opt.value ? ' selected' : ''}`}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
// --------------------------------------------------------

// --- Custom grouped dropdown with app-styled scrollbar ---
const InterfaceDropdown = ({ value, options, onChange, disabled, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const ref = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (isOpen) { setTimeout(() => searchRef.current?.focus(), 0); }
        else setSearchQuery('');
    }, [isOpen]);

    const selectedLabel = value ? value.replace(/_/g, ' ') : null;

    const filteredOptions = options.map(drop => ({
        ...drop,
        interfaces: drop.interfaces.filter(item => {
            const q = searchQuery.toLowerCase();
            return item.name.toLowerCase().includes(q) ||
                   item.name.replace(/_/g, ' ').toLowerCase().includes(q);
        }),
    })).filter(drop => drop.interfaces.length > 0);

    return (
        <div className="custom-dropdown" ref={ref}>
            <button
                type="button"
                className={`custom-dropdown-trigger${isOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(p => !p)}
                disabled={disabled}
            >
                <span className={selectedLabel ? '' : 'custom-dropdown-placeholder'}>{selectedLabel || placeholder}</span>
                <span className="custom-dropdown-chevron">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
                <div className="custom-dropdown-list">
                    <div className="custom-dropdown-search">
                        <input
                            ref={searchRef}
                            type="text"
                            className="custom-dropdown-search-input"
                            placeholder="Search interfaces..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Escape') { setIsOpen(false); } e.stopPropagation(); }}
                        />
                    </div>
                    {filteredOptions.length === 0 ? (
                        <div className="custom-dropdown-empty">No interfaces found</div>
                    ) : (
                        filteredOptions.map(drop => (
                            <div key={drop.dropNumber} className="custom-dropdown-group">
                                <div className="custom-dropdown-group-label">{drop.dropName}</div>
                                {drop.interfaces.map(item => (
                                    <div
                                        key={item.name}
                                        className={`custom-dropdown-option${value === item.name ? ' selected' : ''}`}
                                        onClick={() => { onChange(item.name); setIsOpen(false); }}
                                    >
                                        {item.name.replace(/_/g, ' ')}
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
// ----------------------------------------------------------

export default function FileUploadPage() {
    const navigate = useNavigate();
    const { user, selectedProjectName } = useAuth();

    const [projects, setProjects] = useState([]);
    const [interfaces, setInterfaces] = useState([]);
    const [selectedInterface, setSelectedInterface] = useState('');

    const [scheduleData, setScheduleData] = useState([]);

    // --- TAB STATE ---
    const [activeUploadTab, setActiveUploadTab] = useState('single');

    // --- SINGLE UPLOAD STATE ---
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileSubtypes, setFileSubtypes] = useState({});
    const [isSampleUpload, setIsSampleUpload] = useState(false);
    const [allowEmptyBooleans, setAllowEmptyBooleans] = useState(false);
    const [aiReview, setAiReview] = useState(false);

    // --- CROSS-FILE UPLOAD STATE ---
    const [selectedCrossRuleId, setSelectedCrossRuleId] = useState('');
    const [dependentFiles, setDependentFiles] = useState([]);
    const [referenceFiles, setReferenceFiles] = useState([]);

    // --- DRAG AND DROP CROSS-FILE STATES ---
    const [isDragActiveDep, setIsDragActiveDep] = useState(false);
    const [isDragActiveRef, setIsDragActiveRef] = useState(false);

    // --- TOAST NOTIFICATION STATE ---
    const [toastInfo, setToastInfo] = useState({ message: '', id: 0 });

    // --- INTERFACE STATUS DROP COLLAPSE STATE ---
    const [expandedDrops, setExpandedDrops] = useState({});
    const toggleDropCollapse = (dropNumber) => {
        setExpandedDrops(prev => ({ ...prev, [dropNumber]: !prev[dropNumber] }));
    };

    useEffect(() => {
        if (toastInfo.message) {
            const timer = setTimeout(() => setToastInfo({ message: '', id: 0 }), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastInfo]);

    const availableCrossRules = useMemo(() => {
        return ALL_CROSS_RULES.filter(rule =>
            interfaces.includes(rule.dependent) && interfaces.includes(rule.reference)
        );
    }, [interfaces]);

    const activeRule = useMemo(() => availableCrossRules.find(r => r.id === selectedCrossRuleId), [availableCrossRules, selectedCrossRuleId]);

    const crossFilesSwapWarning = useMemo(() => {
        if (!activeRule || dependentFiles.length === 0 || referenceFiles.length === 0) return null;
        const depName = activeRule.dependent;
        const refName = activeRule.reference;
        const matches = (file, ifaceName) => file.name.toLowerCase().replace(/\.csv$/, '').includes(ifaceName);
        const depLooksLikeRef = dependentFiles.some(f => matches(f, refName));
        const refLooksLikeDep = referenceFiles.some(f => matches(f, depName));
        if (depLooksLikeRef && refLooksLikeDep)
            return `The files look swapped — a "${refName.replace(/_/g, ' ')}" file is in the "${depName.replace(/_/g, ' ')}" slot and vice versa. Please verify your inputs before running.`;
        if (depLooksLikeRef)
            return `A file in the "${depName.replace(/_/g, ' ')}" slot looks like a "${refName.replace(/_/g, ' ')}" file. Please verify your inputs.`;
        if (refLooksLikeDep)
            return `A file in the "${refName.replace(/_/g, ' ')}" slot looks like a "${depName.replace(/_/g, ' ')}" file. Please verify your inputs.`;
        return null;
    }, [activeRule, dependentFiles, referenceFiles]);

    // --- SHARED UPLOAD STATE ---
    const [isUploading, setIsUploading] = useState(false);
    const [isChunkPhase, setIsChunkPhase] = useState(false);
    const [bgJobId, setBgJobId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadErrors, setUploadErrors] = useState({});
    const [uploadWarnings, setUploadWarnings] = useState({});
    const [uploadSuccesses, setUploadSuccesses] = useState({});
    const [uploadAiFindings, setUploadAiFindings] = useState({});
    const [isDragActive, setIsDragActive] = useState(false);

    const { addJob, jobs, dismissJob } = useUploadProgress();
    const uploadAbortControllerRef = useRef(null);

    // --- HISTORY STATE ---
    const [uploadHistory, setUploadHistory] = useState([]);
    const [historyFilters, setHistoryFilters] = useState({
        startDate: '',
        endDate: '',
        filename: '',
        status: 'All',
        uploadType: 'All'
    });

    const fileInputRef = useRef(null);
    const crossPollIntervalRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [expandedErrors, setExpandedErrors] = useState({});

    const toggleErrorCard = (key) => {
        setExpandedErrors(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // AI findings collapse — undefined = expanded (default open), false = collapsed
    const [expandedAiFindings, setExpandedAiFindings] = useState({});
    const toggleAiFindings = (name) => setExpandedAiFindings(prev => ({ ...prev, [name]: prev[name] === false }));

    const exportAiFindingsToPdf = (filename, findings, interfaceName) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(18);
        doc.setTextColor(40, 40, 40);
        doc.text('AI Functional Review', 14, 22);

        doc.setFontSize(11);
        doc.setTextColor(100, 100, 100);
        const iface = interfaceName ? interfaceName.replace(/_/g, ' ') : 'N/A';
        doc.text(`Interface: ${iface}`, 14, 32);
        doc.text(`File: ${filename}`, 14, 39);

        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        doc.text(`Generated: ${dateStr}`, 14, 47);

        let yPos = 57;

        doc.setFontSize(11);
        if (findings.has_issues) {
            const count = (findings.findings || []).length;
            doc.setTextColor(180, 140, 30);
            doc.text(`Status: ${count} Finding${count !== 1 ? 's' : ''} Found`, 14, yPos);
        } else {
            doc.setTextColor(30, 160, 100);
            doc.text('Status: No Issues Found', 14, yPos);
        }
        yPos += 10;

        if (findings.summary) {
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            const lines = doc.splitTextToSize(findings.summary, pageWidth - 28);
            doc.text(lines, 14, yPos);
            yPos += lines.length * 6 + 8;
        }

        if (findings.has_issues && (findings.findings || []).length > 0) {
            autoTable(doc, {
                startY: yPos,
                head: [['Severity', 'Category', 'Description']],
                body: findings.findings.map(f => [
                    (f.severity || '').toUpperCase(),
                    f.category || '',
                    f.description || '',
                ]),
                headStyles: { fillColor: [88, 28, 135], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 'auto' },
                },
                alternateRowStyles: { fillColor: [245, 245, 250] },
                styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak' },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 0) {
                        const sev = (data.cell.raw || '').toLowerCase();
                        if (sev === 'critical') {
                            data.cell.styles.textColor = [185, 28, 28];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (sev === 'warning') {
                            data.cell.styles.textColor = [146, 99, 0];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                },
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(180, 180, 180);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
        }

        const safeName = (filename || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');
        doc.save(`AI_Review_${safeName}.pdf`);
    };

    // --- ERROR MODAL STATE ---
    const [errorModalData, setErrorModalData] = useState({ isOpen: false, filename: '', message: '', type: 'error' });
    const [downloadUrls, setDownloadUrls] = useState({});

    // --- NEW: Direct Download for Massive Logs ---
    const handleDownloadFullLog = async (filename, type) => {
        const downloadUrl = downloadUrls[filename];

        if (!downloadUrl) {
            alert("No full error log is available for this file. It may not have exceeded the UI limit.");
            return;
        }

        try {
            // responseType: 'blob' is critical for preventing RAM crashes in the browser
            const response = await axiosInstance.get(downloadUrl, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Validation_${type === 'warning' ? 'Warnings' : 'Errors'}_${filename}.txt`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download the full log:", error);
            alert("Failed to download the full log file. It may have expired on the server.");
        }
    };

    // --- CSV EXPORT LOGIC (NO LIMITS) ---
    const exportErrorsToCSV = (filename, message, type = 'error') => {
        let csvContent = type === 'warning' ? "Warning Category,Warning Detail\n" : "Error Category,Error Detail\n";
        
        try {
            const parsedData = JSON.parse(message);
            parsedData.forEach(category => {
                const catName = category.category.replace(/"/g, '""');
                if (category.patterns.length === 0) {
                    csvContent += `"${catName}",""\n`;
                } else {
                    category.patterns.forEach(pat => {
                        let detail = pat.description;
                        if (pat.total_rows > 0) {
                            const rowStr = `Rows ${pat.rows.join(', ')}` + (pat.total_rows > pat.rows.length ? ` and ${pat.total_rows - pat.rows.length} more rows` : '');
                            let cleanDesc = detail.replace(/^:\s*/, '');
                            if (cleanDesc.includes("is an exact duplicate") && pat.total_rows > 1) {
                                cleanDesc = cleanDesc.replace("is an exact duplicate", "are exact duplicates");
                            }
                            detail = `${rowStr}: ${cleanDesc}`;
                        }
                        csvContent += `"${catName}","${detail.replace(/"/g, '""')}"\n`;
                    });
                }
            });
        } catch (e) {
            csvContent += `"Legacy Format","Please download the full log file to view these errors."\n`;
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename.replace('.csv', '')}_${type}s.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Initial data load
    useEffect(() => {
        const verifyAccessAndFetchData = async () => {
            try {
                if (!user) return;
                if (user.role !== 'admin' && user.role !== 'editor' && user.role !== 'manager') {
                    navigate('/home');
                    return;
                }

                const userProjects = user.projects || [];
                setProjects(userProjects);

                if (!selectedProjectName && userProjects.length === 0) {
                    setInterfaces([]);
                    setScheduleData([]);
                    setUploadHistory([]);
                    setLoading(false);
                } else if (!selectedProjectName && userProjects.length > 0) {
                    setLoading(false);
                }
            } catch (err) {
                setError('Failed to initialize page data or verify access.');
                console.error("Error verifying access:", err);
                setLoading(false);
            }
        };

        if (user) {
            verifyAccessAndFetchData();
        } else {
            setLoading(true);
        }
    }, [user, navigate, selectedProjectName]);

    const fetchProjectData = useCallback(async (projectName, isSilent = false) => {
        if (!projectName) return;

        if (!isSilent) {
            setLoading(true);
        }
        setError(null);

        try {
            const trimmedProjectName = projectName.trim();
            const [scheduleResponse, historyResponse, statusesResponse] = await Promise.all([
                axiosInstance.get(`/api/projects/${trimmedProjectName}/schedule`),
                axiosInstance.get(`/api/uploads/history?project_name=${encodeURIComponent(trimmedProjectName)}&limit=100`),
                axiosInstance.get(`/api/projects/${trimmedProjectName}/interfaces`)
            ]);

            const scheduleDataResult = scheduleResponse.data;
            const statusesData = statusesResponse.data;

            const cancelledInterfaces = new Set(
                statusesData.filter(s => s.cancellation_reason).map(s => s.interface_name)
            );

            const activeSet = new Set();

            scheduleDataResult.forEach(drop => {
                drop.tasks.forEach(task => {
                    if (!cancelledInterfaces.has(task.interface_name) && !task.interface_name.startsWith('output_')) {
                        activeSet.add(task.interface_name);
                    }
                });
            });

            const activeInterfaces = Array.from(activeSet).sort();

            setInterfaces(activeInterfaces);
            setScheduleData(scheduleDataResult);
            setUploadHistory(historyResponse.data || []);

            setSelectedInterface(prev => {
                if (!prev || !activeInterfaces.includes(prev)) {
                    return activeInterfaces.length > 0 ? activeInterfaces[0] : '';
                }
                return prev;
            });

            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) return;

            setError(error.response?.data?.detail || `Failed to load data for ${projectName}.`);
            setInterfaces([]);
            setScheduleData([]);
            setUploadHistory([]);
            setSelectedInterface('');
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedProjectName) {
            fetchProjectData(selectedProjectName);
        } else if (user) {
            setInterfaces([]);
            setSelectedInterface('');
            setScheduleData([]);
            setUploadHistory([]);
            setError(null);
            setLoading(false);
        }
    }, [selectedProjectName, user, fetchProjectData]);

    // Clear cross-validation poll on unmount so it doesn't leak after navigation
    useEffect(() => {
        return () => {
            if (crossPollIntervalRef.current) {
                clearInterval(crossPollIntervalRef.current);
                crossPollIntervalRef.current = null;
            }
        };
    }, []);

    // Block browser-level navigation (refresh / tab close) during chunk sending
    useEffect(() => {
        if (!isChunkPhase) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isChunkPhase]);

    // Re-attach to any background job for the current project when the page loads
    useEffect(() => {
        if (bgJobId || !selectedProjectName || isUploading) return;
        const match = jobs.find(j => j.projectName === selectedProjectName);
        if (match) {
            setBgJobId(match.id);
            setSelectedInterface(match.interfaceName);
            if (match.status === 'processing') {
                setUploadProgress(50 + Math.round((match.progress || 0) / 2));
            }
        }
    }, [selectedProjectName, jobs, bgJobId, isUploading]);

    // Watch the background job for progress updates and final results
    useEffect(() => {
        if (!bgJobId) return;
        const job = jobs.find(j => j.id === bgJobId);
        if (!job) return;

        if (job.status === 'processing') {
            setUploadProgress(50 + Math.round((job.progress || 0) / 2));
            return;
        }

        // Job finished — process results, clean up local state and remove from context
        // (dismissing from context prevents the re-attach loop and clears the header badge)
        const finishedJobId = job.id;
        setBgJobId(null);
        dismissJob(finishedJobId);

        if (selectedProjectName) {
            setTimeout(() => fetchProjectData(selectedProjectName, true), 500);
        }

        if (job.status === 'error') {
            playNotificationSound('error');
            setUploadErrors({ global: job.result?.detail || 'An unexpected error occurred during upload.' });
        } else {
            const data = job.result || {};
            const hasErrors = (data.summary || []).some(r => r.status === 'Error');
            playNotificationSound(hasErrors ? 'error' : 'success');

            const newSuccesses = {};
            const newWarnings = {};
            const newErrors = {};
            const newDownloadUrls = {};
            const newAiFindings = {};

            (data.summary || []).forEach(result => {
                if (result.status === 'Success') {
                    newSuccesses[result.filename] = result.detail;
                } else if (result.status === 'Warning') {
                    newWarnings[result.filename] = result.detail;
                } else {
                    newErrors[result.filename] = result.detail;
                }
                if (result.warning_detail) newWarnings[result.filename] = result.warning_detail;
                if (result.download_url) newDownloadUrls[result.filename] = result.download_url;
                if (result.ai_findings) newAiFindings[result.filename] = result.ai_findings;
            });

            setUploadSuccesses(newSuccesses);
            setUploadWarnings(newWarnings);
            setUploadErrors(newErrors);
            setDownloadUrls(prev => ({ ...prev, ...newDownloadUrls }));
            setUploadAiFindings(newAiFindings);
        }
    }, [jobs, bgJobId, selectedProjectName, fetchProjectData, dismissJob]);

    const failedInterfaces = useMemo(() => {
        // Step 1: Find the absolute newest upload timestamp for each interface
        const latestTimestamps = uploadHistory.reduce((acc, upload) => {
            if ((upload.upload_type || 'Interface File') !== 'Interface File') return acc;

            const currentUploadDate = upload.uploadedAt ? new Date(upload.uploadedAt).getTime() : 0;

            if (!acc[upload.interface_name] || currentUploadDate > acc[upload.interface_name]) {
                acc[upload.interface_name] = currentUploadDate;
            }
            return acc;
        }, {});

        // Step 2: Check the files from that latest upload event
        const failedSet = new Set();
        uploadHistory.forEach(upload => {
            if ((upload.upload_type || 'Interface File') !== 'Interface File') return;

            const currentUploadDate = upload.uploadedAt ? new Date(upload.uploadedAt).getTime() : 0;

            // If the file was uploaded within 5 seconds of the absolute latest upload for this interface
            if (latestTimestamps[upload.interface_name] - currentUploadDate < 5000) {
                if (upload.status === 'Error') {
                    failedSet.add(upload.interface_name);
                }
            }
        });

        return failedSet;
    }, [uploadHistory]);

    const processedInterfacesByDrop = useMemo(() => {
        if (scheduleData.length === 0) return [];

        const getStatusColorClass = (s) => {
            if (!s) return 'status-not-started';
            switch (s.toLowerCase()) {
                case 'completed': return 'status-completed';
                case 'in progress': return 'status-in-progress';
                case 'failed validation': return 'status-failed-validation';
                default: return 'status-not-started';
            }
        };

        return scheduleData
            .map(drop => {
                const dropTasks = drop.tasks || [];
                const dropInterfaces = [...new Set(
                    dropTasks
                        .map(t => t.interface_name)
                        .filter(name => !name.startsWith('output_') && interfaces.includes(name))
                )].sort();

                const items = dropInterfaces.map(ifaceName => {
                    let status;
                    if (failedInterfaces.has(ifaceName)) {
                        status = "Failed Validation";
                    } else {
                        const interfaceTasks = dropTasks.filter(t => t.interface_name === ifaceName);
                        const taskOrder = ["Data Delivery", "Technical Validation", "Upload in RELEX", "Functional Validation", "Connect Integration"];
                        if (interfaceTasks.length === 0) {
                            status = "Not Started";
                        } else {
                            const isFullyCompleted = taskOrder.every(taskName => {
                                const task = interfaceTasks.find(t => t.task_name === taskName);
                                return task && task.status === 'Completed';
                            });
                            const hasAnyProgress = interfaceTasks.some(t => t.status === 'In Progress' || t.status === 'Completed');
                            status = isFullyCompleted ? "Completed" : hasAnyProgress ? "In Progress" : "Not Started";
                        }
                    }
                    return { name: ifaceName, status, statusClass: getStatusColorClass(status) };
                });

                return { dropNumber: drop.drop_number, dropName: drop.name, interfaces: items };
            })
            .filter(drop => drop.interfaces.length > 0);
    }, [scheduleData, interfaces, failedInterfaces]);

    const filteredHistory = useMemo(() => {
        return (uploadHistory || []).filter(item => {
            if (item.interface_name && !interfaces.includes(item.interface_name)) {
                return false;
            }

            let itemDate = null;
            try { itemDate = item.uploadedAt ? new Date(item.uploadedAt) : null; } catch (e) { }

            let startDate = null;
            try { startDate = historyFilters.startDate ? new Date(historyFilters.startDate) : null; if (startDate) startDate.setHours(0, 0, 0, 0); } catch (e) { }

            let endDate = null;
            try { endDate = historyFilters.endDate ? new Date(historyFilters.endDate) : null; if (endDate) endDate.setHours(23, 59, 59, 999); } catch (e) { }

            if (!itemDate) return false;
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;

            if (historyFilters.filename && !(item.filename || '').toLowerCase().includes(historyFilters.filename.toLowerCase())) return false;
            if (historyFilters.status !== 'All' && (item.status || '').toLowerCase() !== historyFilters.status.toLowerCase()) return false;
            if (historyFilters.uploadType !== 'All') {
                const itemType = item.upload_type || 'Interface File';
                if (itemType !== historyFilters.uploadType) return false;
            }
            return true;
        });
    }, [uploadHistory, historyFilters, interfaces]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setHistoryFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleFileSelect = useCallback((files) => {
        setUploadErrors({});
        setUploadWarnings({});
        setUploadSuccesses({});
        setUploadAiFindings({});
        const newValidFiles = [];
        const currentErrors = {};
        Array.from(files).forEach(file => {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                currentErrors[file.name] = 'Invalid format. Only .csv is allowed.';
                return;
            }
            if (selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
                currentErrors[file.name] = 'This file is already in the list.';
                return;
            }
            file._uid = crypto.randomUUID();
            newValidFiles.push(file);
        });
        if (Object.keys(currentErrors).length > 0) {
            const skipped = Object.entries(currentErrors).map(([name, reason]) => `${name}: ${reason}`);
            setToastInfo({ message: skipped.join(' · '), id: Date.now(), isError: true });
        }

        if (newValidFiles.length > 0) {
            setSelectedFiles(prevFiles => [...prevFiles, ...newValidFiles]);
            // Sub-files are tagged by the reconciliation effect below, which always reads the
            // interface that is selected right now. Detecting them here read a stale
            // selectedInterface (it is not a dependency of this callback) and shipped another
            // interface's prefix to the validator.
            if (Object.keys(currentErrors).length === 0) {
                setToastInfo({ message: `${newValidFiles.length} file(s) added successfully!`, id: Date.now() });
            }
        }
    }, [selectedFiles]);

    // Keeps every staged file tagged with a sub-file that belongs to the interface selected
    // RIGHT NOW. Files can be staged before an interface is picked, and the interface can be
    // changed while files sit in the list; either way a prefix left over from another
    // interface must never reach the validator, which would otherwise fall back to validating
    // the file against every sub-file's columns at once.
    useEffect(() => {
        setFileSubtypes(prev => reconcileFileSubtypes(selectedFiles, selectedInterface, prev));
    }, [selectedInterface, selectedFiles]);

    const handleDrag = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(e.type === "dragenter" || e.type === "dragover"); }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
        }
    }, [handleFileSelect]);

    const handleRemoveFile = useCallback((fileToRemove) => {
        setSelectedFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
        setUploadErrors(prev => { const newErrors = { ...prev }; delete newErrors[fileToRemove.name]; return newErrors; });
        setUploadWarnings(prev => { const newWarnings = { ...prev }; delete newWarnings[fileToRemove.name]; return newWarnings; });
        setUploadSuccesses(prev => { const newSuccesses = { ...prev }; delete newSuccesses[fileToRemove.name]; return newSuccesses; });
        setUploadAiFindings(prev => { const n = { ...prev }; delete n[fileToRemove.name]; return n; });
        setFileSubtypes(prev => { const n = { ...prev }; delete n[fileToRemove._uid]; return n; });
    }, []);

    const handleUploadAll = async () => {
        if (selectedFiles.length === 0 || !selectedProjectName || !selectedInterface) {
            setUploadErrors({ global: 'Please select a project, an interface, and at least one file.' });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setUploadErrors({});
        setUploadWarnings({});
        setUploadSuccesses({});
        setUploadAiFindings({});
        setBgJobId(null);

        const abortController = new AbortController();
        uploadAbortControllerRef.current = abortController;

        try {
            // Block in-app and browser navigation while chunks are in flight
            setIsChunkPhase(true);
            const sent = await sendFilesInChunks(
                selectedFiles,
                abortController.signal,
                done => setUploadProgress(Math.round(done * 50))
            );
            // ?? not ||: a real selection must never be dropped on the way out.
            const uploadMeta = sent.map(({ file, upload_id, total_chunks }) => ({
                upload_id, filename: file.name, total_chunks, file_prefix: fileSubtypes[file._uid] ?? null,
            }));
            setIsChunkPhase(false);
            uploadAbortControllerRef.current = null;
            // Navigation is now free — validation runs on the server

            const response = await axiosInstance.post('/api/upload-finalize/', {
                upload_ids: uploadMeta,
                project_name: selectedProjectName.trim(),
                interface_name: selectedInterface,
                is_sample: isSampleUpload,
                allow_empty_boolean: allowEmptyBooleans,
                ai_review: aiReview,
            });

            const { job_id } = response.data;

            // Hand off to global context so polling survives page navigation
            const contextId = addJob({
                jobId: job_id,
                interfaceName: selectedInterface,
                projectName: selectedProjectName.trim(),
                fileNames: selectedFiles.map(f => f.name),
            });
            setBgJobId(contextId);
            setIsUploading(false);
            setUploadProgress(50);
            setSelectedFiles([]);
            setFileSubtypes({});
            if (fileInputRef.current) fileInputRef.current.value = '';

        } catch (error) {
            setIsChunkPhase(false);
            uploadAbortControllerRef.current = null;
            if (error.response?.status === 401) return;
            if (isCancelledRequest(error)) {
                // User cancelled — reset silently
                setIsUploading(false);
                setSelectedFiles([]);
                setFileSubtypes({});
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }
            setUploadErrors(prev => ({ ...prev, global: describeStartFailure(error, 'Failed to start upload job.') }));
            setIsUploading(false);
            setSelectedFiles([]);
            setFileSubtypes({});
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleCancelUpload = () => {
        if (isChunkPhase && uploadAbortControllerRef.current) {
            uploadAbortControllerRef.current.abort();
            // catch block in handleUploadAll / handleCrossUpload handles state cleanup
        } else if (bgJobId) {
            const jobToCancel = bgJobId;
            setBgJobId(null);
            setUploadProgress(0);
            dismissJob(jobToCancel);
        }
    };

    const handleCrossFileSelect = (type, files) => {
        setUploadErrors({});
        setUploadWarnings({});
        setUploadSuccesses({});
        const validFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.csv'));

        if (validFiles.length !== files.length) {
            setUploadErrors(prev => ({ ...prev, global: 'Some files were ignored. Only .csv is allowed.' }));
        }

        if (validFiles.length > 0) {
            if (type === 'dependent') setDependentFiles(prev => [...prev, ...validFiles]);
            if (type === 'reference') setReferenceFiles(prev => [...prev, ...validFiles]);
            setToastInfo({ message: `${validFiles.length} file(s) added successfully!`, id: Date.now() });
        }
    };

    const handleDragDep = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActiveDep(e.type === "dragenter" || e.type === "dragover");
    };

    const handleDropDep = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActiveDep(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleCrossFileSelect('dependent', e.dataTransfer.files);
        }
    };

    const handleDragRef = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActiveRef(e.type === "dragenter" || e.type === "dragover");
    };

    const handleDropRef = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActiveRef(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleCrossFileSelect('reference', e.dataTransfer.files);
        }
    };

    const handleCrossUpload = async () => {
        if (!selectedCrossRuleId || dependentFiles.length === 0 || referenceFiles.length === 0) {
            setUploadErrors({ global: 'Please select a rule and provide at least one file for both sides.' });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setUploadErrors({});
        setUploadWarnings({});
        setUploadSuccesses({});

        const rule = availableCrossRules.find(r => r.id === selectedCrossRuleId);

        const abortController = new AbortController();
        uploadAbortControllerRef.current = abortController;

        try {
            // Chunked like single uploads: sent as one request, big files (e.g. a 2 GB
            // product_locations) outlasted Railway's 5-minute upload window.
            setIsChunkPhase(true);
            const sent = await sendFilesInChunks(
                [...dependentFiles, ...referenceFiles],
                abortController.signal,
                done => setUploadProgress(Math.round(done * 50))
            );
            setIsChunkPhase(false);
            uploadAbortControllerRef.current = null;

            const toMeta = ({ file, upload_id, total_chunks }) => ({ upload_id, filename: file.name, total_chunks });
            const response = await axiosInstance.post('/api/cross-validate-finalize/', {
                project_name: selectedProjectName.trim(),
                dependent_interface: rule.dependent,
                reference_interface: rule.reference,
                is_sample: isSampleUpload,
                dependent_uploads: sent.slice(0, dependentFiles.length).map(toMeta),
                reference_uploads: sent.slice(dependentFiles.length).map(toMeta),
            });
            const { job_id } = response.data;

            crossPollIntervalRef.current = setInterval(async () => {
                try {
                    const statusRes = await axiosInstance.get(`/api/upload-status/${job_id}`);
                    const data = statusRes.data;

                    // Sending the files took the first half of the bar
                    setUploadProgress(50 + Math.round((data.progress || 0) / 2));

                    if (data.status === "completed" || data.status === "error") {
                        clearInterval(crossPollIntervalRef.current);
                        crossPollIntervalRef.current = null;
                        setIsUploading(false);

                        if (selectedProjectName) {
                            setTimeout(() => fetchProjectData(selectedProjectName, true), 500);
                        }

                        if (data.status === "error") {
                            playNotificationSound('error');
                            setUploadErrors({ global: data.detail || 'An unexpected error.' });
                        } else {
                            const hasErrors = (data.summary || []).some(r => r.status === "Error");
                            playNotificationSound(hasErrors ? 'error' : 'success');

                            const newSuccesses = {};
                            const newWarnings = {};
                            const newErrors = {};
                            const newDownloadUrls = {};

                            (data.summary || []).forEach(result => {
                                if (result.status === "Success") {
                                    newSuccesses[result.filename] = result.detail;
                                } else if (result.status === "Warning") {
                                    newWarnings[result.filename] = result.detail;
                                } else {
                                    newErrors[result.filename] = result.detail;
                                }
                                if (result.download_url) {
                                    newDownloadUrls[result.filename] = result.download_url;
                                }
                            });

                            setUploadSuccesses(newSuccesses);
                            setUploadWarnings(newWarnings);
                            setUploadErrors(newErrors);
                            setDownloadUrls(prev => ({ ...prev, ...newDownloadUrls }));
                        }
                    }
                } catch (pollErr) {
                    clearInterval(crossPollIntervalRef.current);
                    crossPollIntervalRef.current = null;
                    setIsUploading(false);
                    if (pollErr.response?.status === 401) {
                        setUploadErrors(prev => ({ ...prev, global: 'Session expired. Please log in again to check results.' }));
                    } else {
                        setUploadErrors(prev => ({ ...prev, global: 'Lost connection while polling for results. Please check upload history.' }));
                    }
                }
            }, 1000);

        } catch (error) {
            setIsChunkPhase(false);
            uploadAbortControllerRef.current = null;
            if (error.response?.status === 401) return;
            setIsUploading(false);
            // User cancelled — keep the selected files so the run can be retried
            if (isCancelledRequest(error)) return;
            setUploadErrors(prev => ({ ...prev, global: describeStartFailure(error, 'Failed to start cross-validation job.') }));
        }
    };

    const handleExport = (format) => {
        if (!filteredHistory || filteredHistory.length === 0) {
            alert("There is no data to export.");
            return;
        }

        const dataToExport = filteredHistory.map(item => ({
            "File Name": item.filename || 'N/A',
            "Interface": item.interface_name ? item.interface_name.replace(/_/g, ' ') : 'N/A',
            "Type": item.upload_type || 'Interface File',
            "Date": item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : 'N/A',
            "Status": item.status || 'N/A',
            "Uploader": item.uploader || 'N/A'
        }));

        if (format === 'csv') {
            const headers = Object.keys(dataToExport[0]);
            const csvContent = [
                headers.join(','),
                ...dataToExport.map(row =>
                    headers.map(header => `"${String(row[header] === undefined || row[header] === null ? '' : row[header]).replace(/"/g, '""')}"`).join(',')
                )
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${selectedProjectName || 'export'}_upload_history.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text(`Upload History for ${selectedProjectName || 'Selected Project'}`, 14, 15);
            autoTable(doc, {
                head: [Object.keys(dataToExport[0])],
                body: dataToExport.map(row => Object.values(row).map(val => val === undefined || val === null ? '' : String(val))),
                startY: 20,
            });
            doc.save(`${selectedProjectName || 'export'}_upload_history.pdf`);
        }
    };

    if (!user && loading) return (
        <div className="loading-container initial-load">
            <FaSpinner className="loading-spinner" />
            <p>Loading user data...</p>
        </div>
    );

    if (error && !projects.length && !loading) return (
        <div className="upload-page-container">
            <Header />
            <main className="upload-main-content">
                <div className="feedback-message error" style={{ marginTop: '2rem' }}>
                    <FaExclamationTriangle className="feedback-icon" />
                    <div className="feedback-text-content">
                        <strong>Error:</strong> {error} Please try refreshing the page.
                    </div>
                </div>
            </main>
            <style>{STYLES}</style>
        </div>
    );

    return (
        <div className="upload-page-container">
            {toastInfo.message && (
                <div key={toastInfo.id} className={`toast-notification${toastInfo.isError ? ' toast-error' : ''}`}>
                    {toastInfo.isError ? <FaExclamationTriangle size={18} /> : <FaCheckCircle size={18} />}
                    {toastInfo.message}
                </div>
            )}

            <Header />

            <main className="upload-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>

                    <div className="header-title-group">
                        <h1 className="page-title">Interface Upload</h1>
                        {selectedProjectName && (
                            <div className="project-context-inline">
                                <span className="separator">|</span>
                                <span className="context-label">Project:</span>
                                <span className="project-highlight">{selectedProjectName}</span>
                            </div>
                        )}
                    </div>
                </div>

                {loading && selectedProjectName ? (
                    <div className="loading-container project-load">
                        <FaSpinner className="loading-spinner" />
                        <p>Loading data for {selectedProjectName}...</p>
                    </div>
                ) : error ? (
                    <div className="feedback-message error" style={{ marginTop: '2rem' }}>
                        <FaExclamationTriangle className="feedback-icon" />
                        <div className="feedback-text-content">
                            <strong>Error loading project data:</strong> {error}
                        </div>
                    </div>
                ) : !selectedProjectName && projects.length > 0 ? (
                    <div className="info-message">Please select a project from the header to begin.</div>
                ) : !selectedProjectName && projects.length === 0 ? (
                    <div className="info-message">You are not assigned to any projects.</div>
                ) : (
                    <div className="content-grid">

                        <div className="grid-col-span-2">
                            <div className="content-card">

                                <div className="upload-tabs">
                                    <button
                                        className={`upload-tab ${activeUploadTab === 'single' ? 'active' : ''}`}
                                        onClick={() => { setActiveUploadTab('single'); setUploadErrors({}); setUploadWarnings({}); setUploadSuccesses({}); }}
                                    >
                                        Single Interface Upload
                                    </button>
                                    <button
                                        className={`upload-tab ${activeUploadTab === 'cross' ? 'active' : ''}`}
                                        onClick={() => { setActiveUploadTab('cross'); setUploadErrors({}); setUploadWarnings({}); setUploadSuccesses({}); }}
                                    >
                                        Cross-File Validation
                                    </button>
                                </div>

                                {activeUploadTab === 'single' && (
                                    <div className="tab-content">
                                        <div className="interface-selector-container">
                                            <label htmlFor="interface-selector">Upload files for interface:</label>
                                            <InterfaceDropdown
                                                value={selectedInterface}
                                                options={processedInterfacesByDrop}
                                                onChange={name => setSelectedInterface(name)}
                                                disabled={!interfaces.length}
                                                placeholder="-- Choose an interface --"
                                            />
                                        </div>

                                        <div className={`drop-zone ${isDragActive ? 'active' : ''}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                                            <input
                                                type="file"
                                                id="file-input"
                                                className="file-input-hidden"
                                                accept=".csv"
                                                multiple
                                                onChange={(e) => {
                                                    handleFileSelect(e.target.files);
                                                    e.target.value = null;
                                                }}
                                                ref={fileInputRef}
                                            />
                                            <FaUpload className="drop-zone-icon" />
                                            <div className="drop-zone-prompt">
                                                <p>Drag & drop your .csv files here</p>
                                                <label htmlFor="file-input" className="browse-button">Browse Files</label>
                                            </div>
                                        </div>

                                        {selectedFiles.length > 0 && (
                                            <div className="selected-files-container">
                                                <h3 className="selected-files-title">Files to Upload ({selectedFiles.length})</h3>
                                                <ul className="selected-files-list">
                                                    {selectedFiles.map((file, index) => {
                                                        const subFileOptions = getSubFileOptions(selectedInterface);
                                                        const showDropdown = subFileOptions && subFileOptions.length > 1;
                                                        return (
                                                            <li key={`${file.name}-${index}`} className="selected-file-item">
                                                                <FaFileCsv />
                                                                <span className="selected-file-name">{file.name}</span>
                                                                {/* The ?? fallback only covers the single paint before the
                                                                    reconciliation effect writes the real value. It must not become
                                                                    a standing fallback, or the box shows an option the state never
                                                                    held - which is what hid the stale-prefix bug. */}
                                                                {showDropdown && (
                                                                    <select
                                                                        className="file-subtype-select"
                                                                        value={fileSubtypes[file._uid] ?? subFileOptions[0]}
                                                                        onChange={e => setFileSubtypes(prev => ({ ...prev, [file._uid]: e.target.value }))}
                                                                    >
                                                                        {subFileOptions.map(opt => (
                                                                            <option key={opt} value={opt}>{opt}</option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                                <button onClick={() => handleRemoveFile(file)} className="remove-file-button"><FaTimes /></button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                                <div className="upload-options" style={{ gap: '2rem' }}>
                                                    {/* Sample Upload Checkbox */}
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            id="sample-upload-checkbox"
                                                            checked={isSampleUpload}
                                                            onChange={(e) => setIsSampleUpload(e.target.checked)}
                                                        />
                                                        <label htmlFor="sample-upload-checkbox">
                                                            Skip Project Status Updates
                                                            <span className="tooltip">?
                                                                <span className="tooltip-text">Uploads with this option checked will not affect the project status.</span>
                                                            </span>
                                                        </label>
                                                    </div>

                                                    {/* Allow Empty Booleans Checkbox */}
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            id="empty-boolean-checkbox"
                                                            checked={allowEmptyBooleans}
                                                            onChange={(e) => setAllowEmptyBooleans(e.target.checked)}
                                                        />
                                                        <label htmlFor="empty-boolean-checkbox">
                                                            Allow Empty Booleans
                                                            <span className="tooltip">?
                                                                <span className="tooltip-text">If checked, empty values in Boolean (True/False) columns will be accepted instead of triggering a missing data error.</span>
                                                            </span>
                                                        </label>
                                                    </div>

                                                    {/* AI Functional Review Checkbox */}
                                                    {/*
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            id="ai-review-checkbox"
                                                            checked={aiReview}
                                                            onChange={(e) => setAiReview(e.target.checked)}
                                                        />
                                                        <label htmlFor="ai-review-checkbox">
                                                            AI Functional Review
                                                            <span className="tooltip">?
                                                                <span className="tooltip-text">Uses AI to analyse a sample and statistics from the file, looking for business logic issues such as unexpected value distributions, anomalies, or data quality problems beyond structural validation.</span>
                                                            </span>
                                                        </label>
                                                    </div>
                                                    */}
                                                </div>
                                                <button onClick={handleUploadAll} className="upload-button" disabled={!selectedInterface || isUploading}>
                                                    {isUploading ? <><FaSpinner className="button-spinner" /> Uploading...</> : `Upload All to ${selectedInterface ? selectedInterface.replace(/_/g, ' ') : '...'}`}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeUploadTab === 'cross' && (
                                    <div className="tab-content">
                                        <div className="info-message" style={{ margin: '0 0 1.5rem 0', padding: '1rem', textAlign: 'left' }}>
                                            <p style={{ margin: '0 0 0.5rem 0' }}>
                                                Select a validation rule and upload one or multiple files for each side. The system will merge all records before checking for missing references.
                                            </p>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: 'var(--yellow-color)', fontSize: '0.9rem' }}>
                                                <FaExclamationTriangle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                                                <p style={{ margin: 0, lineHeight: 1.4 }}>
                                                    <strong>Note:</strong> For best results, cross-interface validation should be done with correct and previously validated files!
                                                </p>
                                            </div>
                                        </div>

                                        <div className="interface-selector-container">
                                            <label htmlFor="rule-selector">Validation Rule:</label>
                                            <FlatDropdown
                                                value={selectedCrossRuleId}
                                                options={availableCrossRules.map(rule => ({ value: rule.id, label: rule.label }))}
                                                onChange={val => {
                                                    setSelectedCrossRuleId(val);
                                                    setDependentFiles([]);
                                                    setReferenceFiles([]);
                                                    setUploadErrors({});
                                                    setUploadWarnings({});
                                                    setUploadSuccesses({});
                                                }}
                                                disabled={!availableCrossRules.length}
                                                placeholder="-- Choose a rule based on active interfaces --"
                                            />
                                        </div>

                                        {activeRule && (
                                            <div className="cross-file-inputs">
                                                <div className="cross-file-row">
                                                    <div className="cross-file-label" style={{ textTransform: 'capitalize' }}>
                                                        <span style={{ color: '#ff8a80', marginRight: '4px' }}>*</span>
                                                        {activeRule.dependent.replace(/_/g, ' ')}
                                                    </div>
                                                    <div style={{ flexGrow: 1 }}>
                                                        <input
                                                            type="file"
                                                            id="cross-dep-input"
                                                            className="file-input-hidden"
                                                            accept=".csv"
                                                            multiple
                                                            onChange={(e) => {
                                                                handleCrossFileSelect('dependent', e.target.files);
                                                                e.target.value = null;
                                                            }}
                                                        />
                                                        <label
                                                            htmlFor="cross-dep-input"
                                                            className={`cross-file-drop ${isDragActiveDep ? 'active' : ''}`}
                                                            onDragEnter={handleDragDep}
                                                            onDragLeave={handleDragDep}
                                                            onDragOver={handleDragDep}
                                                            onDrop={handleDropDep}
                                                        >
                                                            <FaUpload style={{ marginRight: '8px' }} /> Drag & drop or click to add <strong style={{ color: 'var(--green-accent)', padding: '0 4px', textTransform: 'capitalize' }}>{activeRule.dependent.replace(/_/g, ' ')}</strong> files
                                                        </label>
                                                        {dependentFiles.length > 0 && (
                                                            <ul className="selected-files-list" style={{ marginTop: '10px', maxHeight: '100px' }}>
                                                                {dependentFiles.map((f, i) => (
                                                                    <li key={i} className="selected-file-item">
                                                                        <FaFileCsv /> <span>{f.name}</span>
                                                                        <button onClick={() => setDependentFiles(prev => prev.filter((_, idx) => idx !== i))} className="remove-file-button"><FaTimes /></button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="cross-file-row">
                                                    <div className="cross-file-label" style={{ textTransform: 'capitalize' }}>
                                                        <span style={{ color: '#ff8a80', marginRight: '4px' }}>*</span>
                                                        {activeRule.reference.replace(/_/g, ' ')}
                                                    </div>
                                                    <div style={{ flexGrow: 1 }}>
                                                        <input
                                                            type="file"
                                                            id="cross-ref-input"
                                                            className="file-input-hidden"
                                                            accept=".csv"
                                                            multiple
                                                            onChange={(e) => {
                                                                handleCrossFileSelect('reference', e.target.files);
                                                                e.target.value = null;
                                                            }}
                                                        />
                                                        <label
                                                            htmlFor="cross-ref-input"
                                                            className={`cross-file-drop ${isDragActiveRef ? 'active' : ''}`}
                                                            onDragEnter={handleDragRef}
                                                            onDragLeave={handleDragRef}
                                                            onDragOver={handleDragRef}
                                                            onDrop={handleDropRef}
                                                        >
                                                            <FaUpload style={{ marginRight: '8px' }} /> Drag & drop or click to add <strong style={{ color: 'var(--green-accent)', padding: '0 4px', textTransform: 'capitalize' }}>{activeRule.reference.replace(/_/g, ' ')}</strong> files
                                                        </label>
                                                        {referenceFiles.length > 0 && (
                                                            <ul className="selected-files-list" style={{ marginTop: '10px', maxHeight: '100px' }}>
                                                                {referenceFiles.map((f, i) => (
                                                                    <li key={i} className="selected-file-item">
                                                                        <FaFileCsv /> <span>{f.name}</span>
                                                                        <button onClick={() => setReferenceFiles(prev => prev.filter((_, idx) => idx !== i))} className="remove-file-button"><FaTimes /></button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {crossFilesSwapWarning && (
                                            <div className="feedback-message" style={{ backgroundColor: 'rgba(245,158,11,0.1)', borderColor: 'var(--yellow-color)', color: 'var(--yellow-color)', marginBottom: '1rem' }}>
                                                <FaExclamationTriangle className="feedback-icon" style={{ flexShrink: 0 }} />
                                                <div className="feedback-text-content"><strong>Possible Input Swap:</strong> {crossFilesSwapWarning}</div>
                                            </div>
                                        )}

                                        <div className="upload-options">
                                            <input type="checkbox" id="cross-sample-upload-checkbox" checked={isSampleUpload} onChange={(e) => setIsSampleUpload(e.target.checked)} />
                                            <label htmlFor="cross-sample-upload-checkbox"> Mark as Sample Upload <span className="tooltip">?<span className="tooltip-text">Sample uploads are for validation checks only. They will not affect the project status.</span></span></label>
                                        </div>

                                        <div style={{ marginTop: '1.5rem' }}>
                                            <button
                                                onClick={handleCrossUpload}
                                                className="upload-button"
                                                disabled={isUploading || !selectedCrossRuleId || dependentFiles.length === 0 || referenceFiles.length === 0}
                                            >
                                                {isUploading ? <><FaSpinner className="button-spinner" /> Running Validation...</> : `Run Cross-Validation`}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="upload-feedback-area">
                                    {isUploading && (
                                        <div className="feedback-message progress" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <FaSpinner className="feedback-icon button-spinner" />
                                                <div className="feedback-text-content">
                                                    <strong>{isChunkPhase ? 'Uploading files…' : 'Finalizing…'}</strong>
                                                    <br />
                                                    {isChunkPhase
                                                        ? `Sending chunks (${uploadProgress}%) — please stay on this page`
                                                        : 'Preparing validation job…'}
                                                </div>
                                            </div>
                                            <div className="progress-bar-container">
                                                <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        </div>
                                    )}

                                    {bgJobId && !isUploading && (
                                        <div className="feedback-message progress" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <FaSpinner className="feedback-icon button-spinner" />
                                                <div className="feedback-text-content" style={{ flex: 1 }}>
                                                    <strong>Validation running in background ({uploadProgress}%)</strong>
                                                    <br />
                                                    You can navigate away freely — results will appear here when done.
                                                </div>
                                                <button className="cancel-bg-upload-btn" onClick={handleCancelUpload}>Cancel</button>
                                            </div>
                                            <div className="progress-bar-container">
                                                <div className="progress-bar-fill processing" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        </div>
                                    )}

                                    {uploadErrors.global && (
                                        <div key="global-error" className="feedback-message error">
                                            <FaExclamationTriangle className="feedback-icon" />
                                            <div className="feedback-text-content">
                                                <strong>Global Error:</strong> {uploadErrors.global}
                                            </div>
                                        </div>
                                    )}

                                    {/* --- RENDER RESULTS GROUPED BY FILE --- */}
                                    {[
                                        ...Object.keys(uploadSuccesses),
                                        ...Object.keys(uploadErrors).filter(k => k !== 'global'),
                                        ...Object.keys(uploadWarnings),
                                    ].filter((v, i, a) => a.indexOf(v) === i).map(name => {
                                        const successMsg = uploadSuccesses[name];
                                        const errorMsg = uploadErrors[name];
                                        const warningMsg = uploadWarnings[name];

                                        const renderAccordionBody = (parsedData) => parsedData.map((category, gIdx) => {
                                            const displayPatterns = category.patterns.slice(0, 10);
                                            const hiddenPatterns = category.total_patterns - displayPatterns.length;
                                            return (
                                                <div key={gIdx} className="error-group-block">
                                                    <div className="error-group-title">{gIdx + 1}. {category.category}</div>
                                                    {displayPatterns.length > 0 && (
                                                        <ul className="error-group-list">
                                                            {displayPatterns.map((pat, pIdx) => {
                                                                if (pat.total_rows === 0) return <li key={pIdx}>{pat.description}</li>;
                                                                const displayRows = pat.rows.slice(0, 10);
                                                                const hiddenRows = pat.total_rows - displayRows.length;
                                                                const rowStr = `Rows ${displayRows.join(', ')}` + (hiddenRows > 0 ? ` and ${hiddenRows.toLocaleString()} more rows` : '');
                                                                let cleanDesc = pat.description.replace(/^:\s*/, '');
                                                                if (cleanDesc.includes("is an exact duplicate")) {
                                                                    if (pat.total_rows > 1) cleanDesc = cleanDesc.replace("is an exact duplicate", "are exact duplicates");
                                                                    return <li key={pIdx}>{rowStr} {cleanDesc}</li>;
                                                                }
                                                                return <li key={pIdx}>{rowStr}: {cleanDesc}</li>;
                                                            })}
                                                        </ul>
                                                    )}
                                                    {hiddenPatterns > 0 && (
                                                        <div className="more-errors-text" style={{ margin: '0.25rem 0 0.5rem 0', fontWeight: '500', fontStyle: 'italic', color: 'var(--light-text)' }}>
                                                            ... and {hiddenPatterns.toLocaleString()} more unique patterns omitted.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });

                                        const aiFindings = uploadAiFindings[name];

                                        return (
                                            <div key={name}>
                                                {successMsg && (
                                                    <div className="feedback-message success">
                                                        <FaCheckCircle className="feedback-icon" />
                                                        <div className="feedback-text-content">
                                                            <strong>{name}:</strong> {successMsg}
                                                        </div>
                                                    </div>
                                                )}

                                                {errorMsg && (() => {
                                                    const isExpanded = expandedErrors[name];
                                                    let parsedData = [];
                                                    try { parsedData = JSON.parse(errorMsg); } catch(e) { parsedData = []; }
                                                    const categoryCount = parsedData.length;
                                                    return (
                                                        <div className="error-accordion-card">
                                                            <div className="error-accordion-header" onClick={() => toggleErrorCard(name)}>
                                                                <div className="error-header-title">
                                                                    <FaExclamationTriangle className="feedback-icon" style={{ color: '#fca5a5' }} />
                                                                    <strong>{name}</strong>
                                                                    <span className="error-badge">{categoryCount} Issue{categoryCount !== 1 ? 's' : ''} Found</span>
                                                                </div>
                                                                <div className="error-header-actions">
                                                                    <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
                                                                </div>
                                                            </div>
                                                            {isExpanded && (
                                                                <div className="error-accordion-body">
                                                                    <div className="absolute-actions-row">
                                                                        <button onClick={() => setErrorModalData({ isOpen: true, filename: name, message: errorMsg, type: 'error' })} className="view-errors-btn">
                                                                            View All Errors
                                                                        </button>
                                                                    </div>
                                                                    {renderAccordionBody(parsedData)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {warningMsg && (() => {
                                                    const isExpanded = expandedErrors[`warning-${name}`];
                                                    let parsedData = [];
                                                    try { parsedData = JSON.parse(warningMsg); } catch(e) { parsedData = []; }
                                                    const categoryCount = parsedData.length;
                                                    return (
                                                        <div className="error-accordion-card warning-accordion-card">
                                                            <div className="error-accordion-header warning-accordion-header" onClick={() => toggleErrorCard(`warning-${name}`)}>
                                                                <div className="error-header-title">
                                                                    <FaExclamationTriangle className="feedback-icon" style={{ color: '#fcd34d' }} />
                                                                    <strong>{name}</strong>
                                                                    <span className="error-badge warning-badge">{categoryCount} Warning{categoryCount !== 1 ? 's' : ''} Found</span>
                                                                </div>
                                                                <div className="error-header-actions">
                                                                    <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
                                                                </div>
                                                            </div>
                                                            {isExpanded && (
                                                                <div className="error-accordion-body warning-accordion-body">
                                                                    <div className="absolute-actions-row">
                                                                        <button onClick={() => setErrorModalData({ isOpen: true, filename: name, message: warningMsg, type: 'warning' })} className="view-errors-btn warning-btn">
                                                                            View All Warnings
                                                                        </button>
                                                                    </div>
                                                                    {renderAccordionBody(parsedData)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {aiFindings && (
                                                    <div className="ai-findings-card">
                                                        <div className="ai-findings-header" onClick={() => toggleAiFindings(name)} style={{ cursor: 'pointer' }}>
                                                            <span className="ai-findings-title">
                                                                <span className="ai-findings-icon">✦</span>
                                                                AI Functional Review
                                                            </span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <span className={`ai-findings-badge ${aiFindings.has_issues ? 'has-issues' : 'no-issues'}`}>
                                                                    {aiFindings.has_issues ? `${(aiFindings.findings || []).length} Finding${(aiFindings.findings || []).length !== 1 ? 's' : ''}` : 'No Issues Found'}
                                                                </span>
                                                                <button
                                                                    className="export-pdf-btn"
                                                                    onClick={(e) => { e.stopPropagation(); exportAiFindingsToPdf(name, aiFindings, selectedInterface); }}
                                                                    title="Export AI Findings to PDF"
                                                                >
                                                                    <FaFilePdf /> Export PDF
                                                                </button>
                                                                <span className="toggle-icon">{expandedAiFindings[name] === false ? '▼' : '▲'}</span>
                                                            </div>
                                                        </div>
                                                        {expandedAiFindings[name] !== false && (
                                                            <>
                                                                {aiFindings.summary && (
                                                                    <p className="ai-findings-summary">{aiFindings.summary}</p>
                                                                )}
                                                                {aiFindings.has_issues && (aiFindings.findings || []).length > 0 && (
                                                                    <ul className="ai-findings-list">
                                                                        {aiFindings.findings.map((f, idx) => (
                                                                            <li key={idx} className={`ai-finding-item severity-${f.severity}`}>
                                                                                <div className="ai-finding-header">
                                                                                    <span className="ai-finding-severity">{f.severity?.toUpperCase()}</span>
                                                                                    <span className="ai-finding-category">{f.category}</span>
                                                                                </div>
                                                                                <p className="ai-finding-desc">{f.description}</p>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Interface Status */}
                        <div className="grid-col-span-1">
                            <div className="content-card">
                                <h2 className="section-title">Interface Status</h2>
                                {processedInterfacesByDrop.length > 0 ? (
                                    <div className="drop-sections-container">
                                        {processedInterfacesByDrop.map(drop => (
                                            <div key={drop.dropNumber} className="drop-section">
                                                <button
                                                    className="drop-section-header"
                                                    onClick={() => toggleDropCollapse(drop.dropNumber)}
                                                >
                                                    <span className="drop-section-title">{drop.dropName}</span>
                                                    <span className="drop-section-meta">
                                                        <span className="drop-interface-count">{drop.interfaces.length} interface{drop.interfaces.length !== 1 ? 's' : ''}</span>
                                                        <span className="drop-chevron">{expandedDrops[drop.dropNumber] ? '▼' : '▶'}</span>
                                                    </span>
                                                </button>
                                                {expandedDrops[drop.dropNumber] && (
                                                    <div className="drop-section-body">
                                                        <table className="styled-table">
                                                            <thead><tr><th>Interface</th><th>Status</th></tr></thead>
                                                            <tbody>
                                                                {drop.interfaces.map(item => (
                                                                    <tr key={item.name}>
                                                                        <td>{item.name.replace(/_/g, ' ')}</td>
                                                                        <td><span className={`status-badge ${item.statusClass}`}>{item.status}</span></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="table-message">No active interfaces found for {selectedProjectName}.</p>
                                )}
                            </div>
                        </div>

                        {/* Upload History */}
                        <div className="grid-col-span-2">
                            <div className="content-card">
                                <div className="history-section-header-row">
                                    <h2 className="section-title-upload-history">Upload History for {selectedProjectName}</h2>
                                    <div className="history-actions-top-right">
                                        <button onClick={() => handleExport('csv')} className="export-button" disabled={!filteredHistory || filteredHistory.length === 0}><FaFileCsv /> Export CSV</button>
                                        <button onClick={() => handleExport('pdf')} className="export-button" disabled={!filteredHistory || filteredHistory.length === 0}><FaFilePdf /> Export PDF</button>
                                    </div>
                                </div>
                                <div className="history-filters">
                                    <div className="filter-group"><label htmlFor="startDate">From:</label><input type="date" id="startDate" name="startDate" value={historyFilters.startDate} onChange={handleFilterChange} /></div>
                                    <div className="filter-group"><label htmlFor="endDate">To:</label><input type="date" id="endDate" name="endDate" value={historyFilters.endDate} onChange={handleFilterChange} /></div>
                                    <div className="filter-group"><label htmlFor="filename">Filename:</label><input type="text" id="filename" name="filename" placeholder="Contains..." value={historyFilters.filename} onChange={handleFilterChange} /></div>
                                    <div className="filter-group"><label htmlFor="uploadType">Type:</label><select id="uploadType" name="uploadType" value={historyFilters.uploadType} onChange={handleFilterChange}><option value="All">All</option><option value="Interface File">Interface</option><option value="Sample File">Sample</option></select></div>
                                    {/* --- UPDATED: Added Warning Option --- */}
                                    <div className="filter-group"><label htmlFor="status">Status:</label><select id="status" name="status" value={historyFilters.status} onChange={handleFilterChange}><option value="All">All</option><option value="Success">Success</option><option value="Warning">Warning</option><option value="Error">Error</option></select></div>
                                </div>

                                <div className="table-wrapper history-table-wrapper">
                                    <table className="styled-table">
                                        <thead><tr><th>File Name</th><th>Interface</th><th>Type</th><th>Date</th><th>Status</th><th>Uploader</th></tr></thead>
                                        <tbody>
                                            {filteredHistory && filteredHistory.length > 0 ? filteredHistory.map(item => (
                                                <tr key={item.id}>
                                                    <td>{item.filename || 'N/A'}</td>
                                                    <td>{item.interface_name ? item.interface_name.replace(/_/g, ' ') : 'N/A'}</td>
                                                    <td>{item.upload_type || 'Interface File'}</td>
                                                    <td>{item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : 'N/A'}</td>
                                                    <td><span className={`status-badge ${(item.status || '').toLowerCase()}`}>{item.status || 'N/A'}</span></td>
                                                    <td>{item.uploader || 'N/A'}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="6" className="table-message">No upload history matches filters.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- DYNAMIC DETAILS MODAL (Now supports Warnings!) --- */}
                {errorModalData.isOpen && (
                    <div className="error-modal-overlay" onClick={() => setErrorModalData({ isOpen: false, filename: '', message: '', type: 'error' })}>
                        <div className="error-modal-content" onClick={e => e.stopPropagation()}>
                            <div className={`error-modal-header ${errorModalData.type === 'warning' ? 'warning-header' : ''}`}>
                                <h3 className="error-modal-title">
                                    <FaExclamationTriangle style={{ color: errorModalData.type === 'warning' ? '#fcd34d' : '#fca5a5', marginRight: '10px' }} />
                                    {errorModalData.type === 'warning' ? 'Warnings for:' : 'Errors for:'} {errorModalData.filename}
                                </h3>
                                <button className="close-modal-btn" onClick={() => setErrorModalData({ isOpen: false, filename: '', message: '', type: 'error' })}>
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="error-modal-actions">
                                <button
                                    onClick={() => {
                                        // Fallback: If no download URL exists, use the old in-memory CSV builder
                                        if (!downloadUrls[errorModalData.filename]) {
                                            exportErrorsToCSV(errorModalData.filename, errorModalData.message, errorModalData.type);
                                        } else {
                                            handleDownloadFullLog(errorModalData.filename, errorModalData.type);
                                        }
                                    }}
                                    className="export-csv-btn"
                                >
                                    <FaFileCsv style={{ fontSize: '1.2rem' }} />
                                    {downloadUrls[errorModalData.filename] ? `Download Full ${errorModalData.type === 'warning' ? 'Warnings' : 'Errors'} Log` : `Export ${errorModalData.type === 'warning' ? 'Warnings' : 'Errors'} to CSV`}
                                </button>
                            </div>

                            <div className="error-modal-body">
                                {(() => {
                                    let parsedData = [];
                                    try { parsedData = JSON.parse(errorModalData.message); } catch(e) { return <div style={{padding: '1rem'}}>Legacy format or error parsing data.</div>; }

                                    return parsedData.map((category, gIdx) => {
                                        const hiddenPatterns = category.total_patterns - category.patterns.length;

                                        return (
                                            <div key={gIdx} className="modal-error-group-block">
                                                <div className={`modal-error-group-title ${errorModalData.type === 'warning' ? 'warning-title' : ''}`}>
                                                    {category.category}
                                                    <span style={{ fontSize: '0.8rem', color: 'var(--light-text)', marginLeft: '10px', fontWeight: 'normal' }}>
                                                        (Total: {category.total_patterns.toLocaleString()} unique {errorModalData.type} patterns)
                                                    </span>
                                                </div>
                                                {(category.patterns.length > 0 || hiddenPatterns > 0) && (
                                                    <div className="modal-error-pre-container">
                                                        <pre className="modal-error-pre-text">
                                                            {category.patterns.map(pat => {
                                                                if (pat.total_rows === 0) return pat.description;
                                                                
                                                                const hiddenRows = pat.total_rows - pat.rows.length;
                                                                const rowStr = `Rows ${pat.rows.join(', ')}` + (hiddenRows > 0 ? ` and ${hiddenRows.toLocaleString()} more rows` : '');

                                                                let cleanDesc = pat.description.replace(/^:\s*/, '');
                                                                if (cleanDesc.includes("is an exact duplicate")) {
                                                                    if (pat.total_rows > 1) cleanDesc = cleanDesc.replace("is an exact duplicate", "are exact duplicates");
                                                                    return `${rowStr} ${cleanDesc}`;
                                                                }
                                                                return `${rowStr}: ${cleanDesc}`;
                                                            }).join('\n')}
                                                            
                                                            {hiddenPatterns > 0 && (
                                                                `\n\n[... ${hiddenPatterns.toLocaleString()} more unique patterns omitted from Modal view ...]`
                                                            )}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Chunk-phase overlay — blocks all pointer interaction while files are being sent */}
            {isChunkPhase && (
                <div className="chunk-phase-overlay">
                    <div className="chunk-phase-banner">
                        <FaSpinner className="button-spinner" style={{ flexShrink: 0 }} />
                        <span>Uploading files ({uploadProgress}%) — please stay on this page.</span>
                        <button className="chunk-cancel-btn" onClick={handleCancelUpload}>Cancel</button>
                    </div>
                </div>
            )}

            <style>{STYLES}</style>
        </div>
    );
}

// --- Styles ---
const STYLES = `
   body { background-color: var(--dark-bg); }
   :root {
       --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
       --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
       --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
       --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1); --success-border: #34d399;
       --progress-color: #60a5fa; --progress-bg: rgba(59, 130, 246, 0.1);
       --blue-bg: rgba(59, 130, 246, 0.1); --blue-color: #93c5fd; 
       --yellow-bg: rgba(245, 158, 11, 0.1); --yellow-color: #fcd34d; 
       --green-bg: rgba(16, 185, 129, 0.1); --green-color: #6ee7b7; 
   }

   .upload-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }

    .dashboard-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 2rem; background-color: var(--card-bg); border-bottom: 1px solid var(--border-color); box-shadow: 0 2px 4px rgba(0,0,0,0.2); gap: 1.5rem; position: sticky; top: 0; z-index: 100; }
    .logo-section { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
    .logo-icon { width: 30px; height: 30px; }
    .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); white-space: nowrap; }
    .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); white-space: nowrap; }
    .header-project-selector-wrapper { display: flex; align-items: center; gap: 0.75rem; flex-grow: 1; min-width: 150px; max-width: 500px; }
    .header-project-selector { display: flex; align-items: center; gap: 0.5rem; flex-grow: 1; }
    .header-project-selector label { font-size: 0.9rem; font-weight: 500; color: var(--light-text); white-space: nowrap; }
    .header-project-selector select { flex-grow: 1; padding: 0.5rem 0.75rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); font-size: 0.9rem; min-width: 100px; cursor: pointer; }
    .header-project-selector select:focus { outline: none; border-color: var(--green-accent); }
    .project-selected-confirmation { display: flex; align-items: center; gap: 0.3rem; color: var(--green-accent); font-size: 0.85rem; font-weight: 600; opacity: 0; transition: opacity 0.3s ease-in-out; white-space: nowrap; }
    .project-selected-confirmation.visible { opacity: 1; }
    .dashboard-header > div:last-child { flex-shrink: 0; }

   .upload-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
   .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
   .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; }
   .back-button:hover { background: #4a5568; }
   .header-title-group { display: flex; align-items: center; flex-grow: 1; flex-wrap: wrap; gap: 1rem; }
   
   .page-title { font-size: 2.2rem; font-weight: 700; color: var(--text-color); margin: 0; line-height: 1; white-space: nowrap; }
   
   .project-context-inline { display: flex; align-items: center; font-size: 1rem; color: var(--light-text); font-weight: 500; white-space: nowrap; }
   .separator { margin-right: 1rem; color: var(--border-color); font-weight: 300; font-size: 1.2rem; position: relative; top: -1px; }
   .context-label { margin-right: 0.5rem; }
   .project-highlight { color: var(--green-accent); font-weight: 600; }

   .content-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 2rem; }
   .grid-col-span-1 { grid-column: span 1; }
   .grid-col-span-2 { grid-column: span 2; }

   .content-card { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; padding: 1.5rem 2rem; width: 87.4vw; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
   .section-title { font-size: 1.25rem; font-weight: 600; margin-top: 0; margin-bottom: 1.5rem; padding-bottom: 0.75rem; color: var(--text-color); border-bottom: 1px solid var(--border-color); }

   .upload-tabs { display: flex; gap: 1rem; margin-bottom: 1.5rem; border-bottom: 1px solid var(--border-color); }
   .upload-tab { padding: 0.75rem 1.5rem; background: transparent; border: none; border-bottom: 2px solid transparent; color: var(--light-text); font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 1rem; margin-bottom: -1px; }
   .upload-tab.active { color: var(--green-accent); border-bottom-color: var(--green-accent); }
   .upload-tab:hover:not(.active) { color: var(--text-color); }
   .file-input-hidden { display: none; }

   .cross-file-inputs { display: flex; flex-direction: column; gap: 1rem; }
   .cross-file-label { flex-basis: 220px; font-weight: 600; color: var(--text-color); flex-shrink: 0;}
   .cross-file-drop { 
       flex-grow: 1; 
       border: 2px dashed var(--border-color); 
       padding: 1.5rem; 
       border-radius: 10px; 
       text-align: center; 
       cursor: pointer; 
       color: var(--light-text); 
       background-color: rgba(26, 32, 44, 0.4);
       transition: all 0.3s ease; 
       display: flex;
       align-items: center;
       justify-content: center;
       gap: 0.75rem;
       font-weight: 500;
       font-size: 1rem;
       line-height: 1;
   }
   .cross-file-drop:hover { 
       border-color: var(--green-accent); 
       background-color: rgba(72, 187, 120, 0.05); 
       color: var(--text-color);
       transform: translateY(-1px);
   }
   .cross-file-drop.active { 
       border-color: var(--green-accent); 
       background-color: rgba(72, 187, 120, 0.15); 
       transform: scale(1.02);
   }
   .cross-file-drop svg { color: var(--green-accent); font-size: 1.3rem; transition: transform 0.3s ease; }
   .cross-file-drop:hover svg { transform: scale(1.15); }
   .cross-file-row { display: flex; align-items: center; gap: 1.5rem; background: transparent; padding: 0.5rem 0; border: none; }

   .interface-selector-container { margin-bottom: 1.5rem; }
   .interface-selector-container label { display: block; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }

   .custom-dropdown { position: relative; width: 100%; }
   .custom-dropdown-trigger { width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); font-size: 1rem; cursor: pointer; text-align: left; transition: border-color 0.2s; }
   .custom-dropdown-trigger:hover:not(.disabled) { border-color: var(--green-accent); }
   .custom-dropdown-trigger.open { border-color: var(--green-accent); border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
   .custom-dropdown-trigger.disabled { opacity: 0.5; cursor: not-allowed; }
   .custom-dropdown-placeholder { color: var(--light-text); }
   .custom-dropdown-chevron { font-size: 0.65rem; color: var(--light-text); flex-shrink: 0; margin-left: 0.5rem; }
   .custom-dropdown-list { position: absolute; top: 100%; left: 0; right: 0; background-color: var(--input-bg); border: 1px solid var(--green-accent); border-top: none; border-bottom-left-radius: 6px; border-bottom-right-radius: 6px; max-height: 320px; overflow-y: auto; z-index: 50; }
   .custom-dropdown-search { position: sticky; top: 0; z-index: 1; padding: 0.5rem; background-color: var(--input-bg); border-bottom: 1px solid var(--border-color); }
   .custom-dropdown-search-input { width: 100%; box-sizing: border-box; background-color: var(--dark-bg); border: 1px solid var(--border-color); border-radius: 4px; color: var(--text-color); font-size: 0.85rem; padding: 0.4rem 0.65rem; outline: none; }
   .custom-dropdown-search-input:focus { border-color: var(--green-accent); }
   .custom-dropdown-empty { padding: 0.75rem 1rem; font-size: 0.875rem; color: var(--light-text); text-align: center; font-style: italic; }
   .custom-dropdown-list::-webkit-scrollbar { width: 6px; }
   .custom-dropdown-list::-webkit-scrollbar-track { background: var(--dark-bg); }
   .custom-dropdown-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 6px; }
   .custom-dropdown-group-label { padding: 0.45rem 0.75rem; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--light-text); background-color: var(--dark-bg); border-top: 1px solid var(--border-color); }
   .custom-dropdown-group:first-child .custom-dropdown-group-label { border-top: none; }
   .custom-dropdown-option { padding: 0.6rem 1rem; cursor: pointer; font-size: 0.95rem; color: var(--text-color); transition: background-color 0.15s; }
   .custom-dropdown-option:hover { background-color: rgba(72, 187, 120, 0.1); color: var(--green-accent); }
   .custom-dropdown-option.selected { background-color: rgba(72, 187, 120, 0.15); color: var(--green-accent); font-weight: 600; }

   .drop-zone { 
       border: 2px dashed #4a5568; 
       border-radius: 12px; 
       padding: 3rem 2rem; 
       text-align: center; 
       color: var(--light-text); 
       transition: all 0.3s ease; 
       cursor: pointer; 
       background-color: rgba(26, 32, 44, 0.4); 
       display: flex;
       flex-direction: column;
       align-items: center;
       justify-content: center;
       gap: 0.5rem;
   }
   .drop-zone:hover { 
       border-color: var(--green-accent); 
       background-color: rgba(72, 187, 120, 0.05); 
       transform: translateY(-2px);
       box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
   }
   .drop-zone.active { 
       border-color: var(--green-accent); 
       background-color: rgba(72, 187, 120, 0.15); 
       transform: scale(1.02);
   }
   .drop-zone-icon { 
       font-size: 3.5rem; 
       color: var(--green-accent); 
       opacity: 0.9; 
       margin-bottom: 0.5rem;
       transition: transform 0.3s ease;
   }
   .drop-zone:hover .drop-zone-icon { transform: translateY(-5px); }
   .drop-zone-prompt p { margin: 0; font-size: 1.05rem; }
   
   .browse-button { 
       background-color: var(--card-bg); 
       border: 1px solid var(--border-color); 
       color: var(--text-color); 
       padding: 0.6rem 1.5rem; 
       border-radius: 8px; 
       cursor: pointer; 
       font-weight: 600; 
       display: inline-block; 
       margin-top: 1rem;
       transition: all 0.2s ease; 
       box-shadow: 0 2px 4px rgba(0,0,0,0.1);
   }
   .drop-zone:hover .browse-button { border-color: var(--green-accent); color: var(--green-accent); }

   /* --- ACCORDION STYLES --- */
   .error-accordion-card {
       background-color: var(--card-bg);
       border: 1px solid var(--danger-color);
       border-radius: 8px;
       overflow: hidden;
       margin-bottom: 0.75rem;
       box-shadow: 0 2px 4px rgba(239, 68, 68, 0.1);
   }
   
   .error-accordion-header {
       background-color: rgba(239, 68, 68, 0.1);
       padding: 1rem 1.25rem;
       display: flex;
       justify-content: space-between;
       align-items: center;
       cursor: pointer;
       transition: background-color 0.2s;
   }
   .error-accordion-header:hover { background-color: rgba(239, 68, 68, 0.15); }
   
   /* --- AI FINDINGS CARD --- */
   .ai-findings-card {
       background-color: var(--card-bg);
       border: 1px solid rgba(139, 92, 246, 0.4);
       border-radius: 8px;
       overflow: hidden;
   }
   .ai-findings-header {
       display: flex;
       align-items: center;
       justify-content: space-between;
       padding: 0.85rem 1.25rem;
       background-color: rgba(139, 92, 246, 0.08);
   }
   .ai-findings-title {
       display: flex;
       align-items: center;
       gap: 0.5rem;
       font-weight: 600;
       color: #c4b5fd;
       font-size: 0.95rem;
   }
   .ai-findings-icon { font-size: 1rem; color: #a78bfa; }
   .ai-findings-badge {
       font-size: 0.75rem;
       font-weight: 700;
       padding: 0.2rem 0.65rem;
       border-radius: 99px;
   }
   .ai-findings-badge.has-issues { background-color: rgba(251, 191, 36, 0.15); color: #fbbf24; border: 1px solid rgba(251, 191, 36, 0.35); }
   .ai-findings-badge.no-issues { background-color: rgba(52, 211, 153, 0.15); color: #34d399; border: 1px solid rgba(52, 211, 153, 0.3); }
   .ai-findings-summary {
       margin: 0;
       padding: 0.75rem 1.25rem;
       font-size: 0.85rem;
       color: var(--light-text);
       border-bottom: 1px solid rgba(139, 92, 246, 0.12);
       line-height: 1.6;
   }
   .ai-findings-list {
       list-style: none;
       padding: 0.75rem 1.25rem;
       margin: 0;
       display: flex;
       flex-direction: column;
       gap: 0.6rem;
   }
   .ai-finding-item {
       border-radius: 6px;
       padding: 0.7rem 0.9rem;
       border-left: 3px solid transparent;
       background-color: rgba(255, 255, 255, 0.03);
   }
   .severity-critical.ai-finding-item {
       border-left-color: #f87171;
       background-color: rgba(239, 68, 68, 0.05);
   }
   .severity-warning.ai-finding-item {
       border-left-color: #fbbf24;
       background-color: rgba(251, 191, 36, 0.05);
   }
   .ai-finding-header {
       display: flex;
       align-items: center;
       gap: 0.55rem;
       margin-bottom: 0.4rem;
   }
   .ai-finding-severity {
       flex-shrink: 0;
       font-size: 0.68rem;
       font-weight: 700;
       padding: 0.15rem 0.45rem;
       border-radius: 4px;
       letter-spacing: 0.06em;
   }
   .severity-critical .ai-finding-severity { background-color: rgba(239, 68, 68, 0.2); color: #fca5a5; }
   .severity-warning .ai-finding-severity { background-color: rgba(251, 191, 36, 0.2); color: #fbbf24; }
   .ai-finding-category {
       font-weight: 600;
       font-size: 0.875rem;
       color: var(--text-color);
   }
   .ai-finding-desc {
       margin: 0;
       font-size: 0.82rem;
       color: var(--light-text);
       line-height: 1.6;
   }
   .export-pdf-btn {
       display: flex;
       align-items: center;
       gap: 0.35rem;
       padding: 0.28rem 0.7rem;
       font-size: 0.75rem;
       font-weight: 600;
       background-color: rgba(139, 92, 246, 0.12);
       color: #c4b5fd;
       border: 1px solid rgba(139, 92, 246, 0.35);
       border-radius: 6px;
       cursor: pointer;
       transition: background-color 0.2s, border-color 0.2s;
       white-space: nowrap;
   }
   .export-pdf-btn:hover {
       background-color: rgba(139, 92, 246, 0.22);
       border-color: rgba(139, 92, 246, 0.6);
   }
   /* ------------------------ */

   /* --- NEW: WARNING ACCORDION STYLES --- */
   .warning-accordion-card {
       border-color: var(--yellow-color);
       box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);
   }
   .warning-accordion-header { background-color: rgba(245, 158, 11, 0.1); }
   .warning-accordion-header:hover { background-color: rgba(245, 158, 11, 0.15); }
   
   .warning-badge { background-color: var(--yellow-color); color: var(--dark-bg); }
   .warning-accordion-body { border-top: 1px solid rgba(245, 158, 11, 0.2); }
   .warning-group-title { color: var(--yellow-color); border-bottom: 1px solid rgba(245, 158, 11, 0.2); }
   .warning-header { background-color: rgba(245, 158, 11, 0.1) !important; border-bottom: 1px solid var(--border-color); }
   .warning-title { color: var(--yellow-color) !important; border-bottom: 1px solid rgba(252, 211, 77, 0.2) !important; }
   .view-errors-btn.warning-btn:hover { border-color: var(--yellow-color); }
   /* ------------------------------------ */

   .error-header-title { display: flex; align-items: center; gap: 0.75rem; color: var(--text-color); font-size: 1rem; }
   
   .error-badge {
       background-color: var(--danger-color);
       color: #fff;
       font-size: 0.75rem;
       font-weight: 700;
       padding: 0.2rem 0.6rem;
       border-radius: 99px;
       margin-left: 0.5rem;
   }
   
   .toggle-icon { color: var(--light-text); font-size: 0.8rem; }
   
   .error-accordion-body {
       padding: 1.25rem;
       border-top: 1px solid rgba(239, 68, 68, 0.2);
       background-color: var(--dark-bg);
       position: relative;
       overflow-wrap: anywhere;
   }

   .absolute-actions-row { position: absolute; top: 1.25rem; right: 1.25rem; z-index: 10; }
   .error-group-block:first-child .error-group-title { padding-right: 140px; }
   
   .copy-error-btn { background-color: transparent; border: 1px solid var(--border-color); color: var(--light-text); padding: 0.4rem 0.8rem; border-radius: 6px; font-size: 0.8rem; cursor: pointer; transition: all 0.2s; }
   .copy-error-btn:hover { background-color: var(--card-bg); color: var(--text-color); border-color: var(--light-text); }
   
   .error-group-block { margin-bottom: 1.25rem; }
   .error-group-block:last-child { margin-bottom: 0; }
   
   .error-group-title { font-weight: 600; color: #fca5a5; margin-bottom: 0.4rem; font-size: 0.95rem; }
   .error-group-list { margin: 0 0 0 1.2rem; padding: 0; list-style-type: disc; color: #e2e8f0; font-size: 0.9rem; line-height: 1.4; }
   .error-group-list li { margin-bottom: 0.3rem; }

   .selected-files-container { margin-top: 1.5rem; background-color: var(--dark-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; }
   .selected-files-title { font-size: 1rem; font-weight: 600; color: var(--text-color); margin: 0 0 1rem 0; }
   .selected-files-list { list-style: none; padding: 0; margin: 0 0 1.5rem 0; max-height: 200px; overflow-y: auto; }
   .selected-files-list::-webkit-scrollbar { width: 6px; }
   .selected-files-list::-webkit-scrollbar-track { background: var(--dark-bg); }
   .selected-files-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 6px; }
   .selected-file-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; border-bottom: 1px solid var(--border-color); font-size: 0.9rem; }
   .selected-file-item:last-child { border-bottom: none; }
   .selected-file-item span { flex-grow: 1; word-break: break-all; }
   .selected-file-name { flex: 1; min-width: 0; word-break: break-all; }
   .file-subtype-select {
       flex-shrink: 0;
       background-color: var(--card-bg);
       color: var(--text-color);
       border: 1px solid rgba(139, 92, 246, 0.4);
       border-radius: 6px;
       padding: 0.25rem 0.5rem;
       font-size: 0.78rem;
       cursor: pointer;
       max-width: 220px;
       outline: none;
   }
   .file-subtype-select:focus { border-color: rgba(139, 92, 246, 0.8); }
   .remove-file-button { background: none; border: none; color: #ef4444; font-size: 1rem; cursor: pointer; transition: color 0.2s; padding: 0; line-height: 1; }
   .remove-file-button:hover { color: #dc2626; }

   .upload-options { display: flex; align-items: center; margin-bottom: 1.5rem; }
   .upload-options input[type="checkbox"] { width: 16px; height: 16px; margin-right: 0.75rem; cursor: pointer; accent-color: var(--green-accent); }
   .upload-options label { display: flex; align-items: center; cursor: pointer; font-weight: 500; color: var(--light-text); font-size: 0.9rem; }
   .tooltip { position: relative; display: inline-flex; align-items: center; justify-content: center; margin-left: 0.5rem; width: 18px; height: 18px; border-radius: 50%; background-color: var(--border-color); color: var(--dark-bg); font-weight: 700; font-size: 0.8rem; cursor: help; }
   .tooltip .tooltip-text { visibility: hidden; width: 220px; background-color: #111827; color: #fff; text-align: center; border-radius: 6px; padding: 8px; position: absolute; z-index: 50; bottom: 135%; left: 50%; margin-left: -110px; opacity: 0; transition: opacity 0.3s; font-weight: 400; font-size: 0.8rem; box-shadow: 0 2px 5px rgba(0,0,0,0.5); }
   .tooltip:hover .tooltip-text { visibility: visible; opacity: 1; }

   .upload-button { background-color: var(--green-accent); color: var(--dark-bg); border: none; padding: 0.8rem 1.5rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.5rem; font-size: 1rem; }
   .upload-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }
   .upload-button:hover:not(:disabled) { background-color: #38a169; }
   .button-spinner { animation: spin 1s linear infinite; }

   .upload-feedback-area { margin-top: 1.5rem; width: 100%; display: flex; flex-direction: column; gap: 0.75rem; }
   .feedback-message { display: flex; align-items: flex-start; gap: 0.75rem; padding: 0.75rem 1rem; border-radius: 6px; font-weight: 500; font-size: 0.9rem; border: 1px solid transparent; }
   .feedback-icon { flex-shrink: 0; margin-top: 2px; font-size: 1.1em; }
   .feedback-text-content { word-break: break-word; }
   .feedback-message.error { background-color: var(--error-bg); color: var(--error-color); border-color: var(--danger-color); }
   .feedback-message.success { background-color: var(--success-bg); color: var(--success-color); border-color: var(--success-border); }
   .feedback-message.progress { background-color: var(--progress-bg); color: var(--progress-color); border-color: #60a5fa; }

   .progress-bar-container { width: 100%; background-color: rgba(255, 255, 255, 0.1); border-radius: 99px; height: 8px; overflow: hidden; }
   .progress-bar-fill { height: 100%; background-color: #60a5fa; transition: width 0.3s ease, background-color 0.3s ease; }
   .progress-bar-fill.processing { background-color: var(--green-accent); animation: progressPulse 1.5s infinite; }
   @keyframes progressPulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }

   .table-wrapper { overflow-x: auto; max-height: 400px; border: 1px solid var(--border-color); border-radius: 8px; margin-bottom: 1.5rem; }
   .table-wrapper::-webkit-scrollbar { width: 8px; height: 8px;}
   .table-wrapper::-webkit-scrollbar-track { background: var(--input-bg); border-radius: 8px;}
   .table-wrapper::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
   .history-table-wrapper { max-height: 500px; }

   .drop-sections-container { display: flex; flex-direction: column; gap: 0.6rem; }
   .drop-section { border: 1px solid var(--border-color); border-radius: 8px; overflow: hidden; }
   .drop-section-header { width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; background-color: var(--input-bg); border: none; cursor: pointer; color: var(--text-color); font-weight: 600; transition: background-color 0.2s; text-align: left; font-size: 0.95rem; }
   .drop-section-header:hover { background-color: #374151; }
   .drop-section-meta { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; }
   .drop-interface-count { font-size: 0.78rem; color: var(--light-text); font-weight: 400; }
   .drop-chevron { font-size: 0.7rem; color: var(--light-text); }
   .drop-section-body { max-height: 280px; overflow-y: auto; }
   .drop-section-body::-webkit-scrollbar { width: 6px; }
   .drop-section-body::-webkit-scrollbar-track { background: var(--dark-bg); }
   .drop-section-body::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 6px; }
   .drop-section-body .styled-table th, .drop-section-body .styled-table td { padding: 0.65rem 1rem; }

   .styled-table { width: 100%; border-collapse: collapse; text-align: left; }
   .styled-table th, .styled-table td { padding: 0.8rem 1rem; border-bottom: 1px solid var(--border-color); vertical-align: middle; white-space: nowrap; }
   .styled-table thead { background-color: var(--input-bg); position: sticky; top: 0; z-index: 10; }
   .styled-table th { font-size: 0.75rem; text-transform: uppercase; color: var(--light-text); }
   .styled-table tbody tr:last-child td { border-bottom: none; }
   .styled-table tbody tr:hover { background-color: #374151; }
   .table-message { text-align: center; padding: 2rem; color: var(--light-text); font-style: italic; }

   .status-badge { padding: 0.25rem 0.75rem; border-radius: 99px; font-weight: 600; font-size: 0.75rem; white-space: nowrap; text-transform: capitalize; border: 1px solid transparent; }
   .status-badge.status-not-started { background-color: var(--blue-bg); color: var(--blue-color); border-color: #60a5fa; }
   .status-badge.status-in-progress { background-color: var(--yellow-bg); color: var(--yellow-color); border-color: #f59e0b; }
   .status-badge.status-completed { background-color: var(--green-bg); color: var(--green-color); border-color: #34d399; }
   .status-badge.status-failed-validation { background-color: var(--error-bg); color: var(--error-color); border-color: var(--danger-color); }
   .status-badge.error { background-color: var(--error-bg); color: var(--error-color); border-color: var(--danger-color); }
   .status-badge.warning { background-color: var(--yellow-bg); color: var(--yellow-color); border-color: #f59e0b; }
   .status-badge.success { background-color: var(--success-bg); color: var(--success-color); border-color: var(--success-border); }


   .history-section-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
   .section-title-upload-history { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; padding: 0; border: none; }
   .history-actions-top-right { display: flex; gap: 1rem; }

   .history-filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; align-items: end; }
   .filter-group { display: flex; flex-direction: column; gap: 0.25rem; }
   .filter-group label { font-size: 0.8rem; font-weight: 500; color: var(--light-text); }
   .filter-group input, .filter-group select { background-color: var(--input-bg); border: 1px solid var(--border-color); color: var(--text-color); padding: 0.6rem; border-radius: 6px; box-sizing: border-box; height: 42px; font-size: 0.9rem; }
   .filter-group select { color-scheme: dark; }

   .export-button { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1.2rem; border: 1px solid var(--border-color); border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; background-color: var(--input-bg); color: var(--text-color); font-size: 0.9rem; }
   .export-button:disabled { cursor: not-allowed; opacity: 0.5; }
   .export-button:hover:not(:disabled) { border-color: var(--green-accent); color: var(--green-accent); background-color: var(--card-bg); }

    /* --- ERROR MODAL STYLES --- */
   .view-errors-btn {
       background-color: transparent;
       border: 1px solid var(--border-color);
       color: var(--light-text);
       padding: 0.5rem 1rem;
       border-radius: 6px;
       font-size: 0.85rem;
       font-weight: 600;
       cursor: pointer;
       transition: all 0.2s;
   }
   .view-errors-btn:hover {
       background-color: var(--card-bg);
       color: var(--text-color);
       border-color: var(--green-accent);
   }

   /* --- Chunk-phase navigation block overlay --- */
   .chunk-phase-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 2000; pointer-events: all; cursor: not-allowed; }
   .chunk-phase-banner { position: fixed; bottom: 1.5rem; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 0.75rem; background-color: #1e3a5f; border: 1px solid #3b82f6; border-radius: 8px; padding: 0.75rem 1.25rem; color: #93c5fd; font-size: 0.9rem; font-weight: 600; box-shadow: 0 4px 16px rgba(0,0,0,0.4); white-space: nowrap; pointer-events: all; cursor: default; z-index: 2001; }
   .chunk-cancel-btn { background-color: rgba(239,68,68,0.15); border: 1px solid #ef4444; color: #fca5a5; border-radius: 6px; padding: 0.3rem 0.75rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background-color 0.2s; flex-shrink: 0; }
   .chunk-cancel-btn:hover { background-color: rgba(239,68,68,0.3); }
   .cancel-bg-upload-btn { background-color: rgba(239,68,68,0.12); border: 1px solid #ef4444; color: #fca5a5; border-radius: 6px; padding: 0.3rem 0.75rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background-color 0.2s; flex-shrink: 0; align-self: flex-start; }
   .cancel-bg-upload-btn:hover { background-color: rgba(239,68,68,0.28); }
   /* --------------------------------------------- */

   .error-modal-overlay {
       position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(15, 23, 42, 0.85); z-index: 1000;
       display: flex; align-items: center; justify-content: center; padding: 2rem; backdrop-filter: blur(4px);
   }

   .error-modal-content {
       background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; width: 100%;
       max-width: 900px; max-height: 85vh; display: flex; flex-direction: column; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
       overflow: hidden; animation: modalFadeIn 0.3s ease-out;
   }

   @keyframes modalFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

   .error-modal-header {
       display: flex; justify-content: space-between; align-items: center; padding: 1.5rem;
       background-color: rgba(239, 68, 68, 0.05); border-bottom: 1px solid var(--border-color);
   }

   .error-modal-title { margin: 0; font-size: 1.25rem; color: var(--text-color); display: flex; align-items: center; word-break: break-all; }
   .close-modal-btn { background: transparent; border: none; color: var(--light-text); font-size: 1.5rem; cursor: pointer; transition: color 0.2s; padding: 0; display: flex; }
   .close-modal-btn:hover { color: var(--danger-color); }

   .error-modal-actions { padding: 1rem 1.5rem; border-bottom: 1px solid var(--border-color); background-color: var(--input-bg); display: flex; justify-content: flex-end; }

   .export-csv-btn {
       display: flex; align-items: center; gap: 0.5rem; background-color: var(--green-accent); color: var(--dark-bg);
       border: none; padding: 0.6rem 1.2rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s;
   }
   .export-csv-btn:hover { background-color: #38a169; }

   .error-modal-body { padding: 1.5rem; overflow-y: auto; background-color: var(--dark-bg); }
   .error-modal-body::-webkit-scrollbar { width: 8px; }
   .error-modal-body::-webkit-scrollbar-track { background: var(--card-bg); }
   .error-modal-body::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }

   .modal-error-group-block { margin-bottom: 1.5rem; }
   .modal-error-group-block:last-child { margin-bottom: 0; }
   .modal-error-group-title { font-weight: 600; color: #fca5a5; margin-bottom: 0.5rem; font-size: 1rem; border-bottom: 1px solid rgba(252, 165, 165, 0.2); padding-bottom: 0.25rem; }
   .modal-error-group-list { margin: 0 0 0 1.5rem; padding: 0; list-style-type: disc; color: #e2e8f0; font-size: 0.95rem; line-height: 1.5; }
   .modal-error-group-list li { margin-bottom: 0.4rem; }

   .more-errors-text { color: #fca5a5; font-style: italic; margin-top: 0.5rem; list-style-type: none; margin-left: -1.2rem; font-weight: 500; }

   .modal-error-pre-container { background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; padding: 1rem; margin-top: 0.5rem; max-height: 400px; overflow-y: auto; }
   .modal-error-pre-container::-webkit-scrollbar { width: 6px; }
   .modal-error-pre-container::-webkit-scrollbar-track { background: var(--dark-bg); }
   .modal-error-pre-container::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 6px; }
   .modal-error-pre-text { margin: 0; color: #e2e8f0; font-family: 'Courier New', Courier, monospace; font-size: 0.85rem; white-space: pre-wrap; word-break: break-all; line-height: 1.5; }

   .loading-container { display:flex; flex-direction:column; align-items:center; justify-content:center; padding: 4rem; color:var(--text-color); background-color: var(--dark-bg); }
   .loading-container.initial-load { min-height: 100vh; }
   .loading-container.project-load { min-height: 50vh; }
   .loading-spinner { animation: spin 1s linear infinite; font-size: 3rem; color: var(--green-accent); }

   @keyframes spin { 100% { transform: rotate(360deg); } }

   .info-message { background-color: var(--card-bg); border: 1px solid var(--border-color); color: var(--light-text); padding: 1.5rem; border-radius: 8px; text-align: center; margin: 2rem; }

   /* --- TOAST NOTIFICATION STYLES --- */
   .toast-notification {
       position: fixed; top: 2rem; right: 2rem; background-color: var(--green-accent); color: var(--dark-bg);
       padding: 1rem 1.5rem; border-radius: 8px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
       display: flex; align-items: center; gap: 0.75rem; font-weight: 600; z-index: 9999;
       max-width: 420px;
       pointer-events: none;
       animation: slideInToast 0.3s cubic-bezier(0.16, 1, 0.3, 1), fadeOutToast 0.3s ease-in 2.7s forwards;
   }
   .toast-notification.toast-error { background-color: #1f1215; color: #fca5a5; border: 1px solid rgba(239, 68, 68, 0.4); }

   @media (max-width: 1024px) {
       .content-grid { grid-template-columns: 1fr; }
       .grid-col-span-1, .grid-col-span-2 { grid-column: span 1; }
   }
   @media (max-width: 768px) {
        .dashboard-header { flex-direction: column; align-items: stretch; padding: 1rem; }
        .header-project-selector-wrapper { max-width: none; margin-top: 0.5rem; margin-bottom: 0.5rem; justify-content: center; }
        .header-project-selector { flex-grow: 0; max-width: 300px; }
        .logo-section { justify-content: center; }
        .project-selected-confirmation { font-size: 0.8rem; }
        .upload-main-content { padding: 0 1rem; }
        .page-title { font-size: 1.8rem; }
        .content-card { padding: 1rem; }
        .history-filters { grid-template-columns: 1fr; }
        .history-section-header-row { flex-direction: column; align-items: flex-start; gap: 1rem; }
        .history-actions-top-right { width: 100%; display: grid; grid-template-columns: 1fr 1fr; }
        .styled-table th, .styled-table td { font-size: 0.85rem; padding: 0.6rem 0.5rem; }
        .cross-file-row { flex-direction: column; align-items: stretch; gap: 0.5rem; }
        .cross-file-label { flex-basis: auto; }
   }
`;